// src/tabs/messages/MessageEditor.jsx — Phase 2.B (ADR-024)
// Édition d'un message : Section Contenu (FR/EN/ES + variables + preview)
// + Section Envois programmés (CRUD inline des message_sends).
//
// Bouton « Traduire automatiquement » (Lot 2) : traduit le FR vers EN + ES via
// l'endpoint admin message_translate (Claude Haiku), remplit les champs À
// L'ÉCRAN (jamais d'écriture DB silencieuse) ; l'admin relit puis Enregistre.

import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import {
  FileText,
  Send,
  AlertTriangle,
  ArrowLeft,
  Languages,
  Save,
  Eye,
  Mail,
  Check,
  Trash2,
  Plus,
  Pencil,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { supabase } from "../../supabase";
// Rowcount-check partagé (ADR-207) — un écrit qui ne touche rien n'est pas
// un succès, même quand PostgREST ne rend aucune erreur.
import { verifierEcriture } from "../../lib/message-sends-garde";
import { verifierSecondairesGabarit } from "../../lib/variables-secondaires-nues";
import { DS } from "../../lib/ui";
import {
  listAvailableVariables,
  renderTemplate,
} from "../../lib/template-variables";
import { getAllLogsFlat } from "../../lib/sousloc-helpers";
import { getActiveMappedLogementIds } from "../../lib/beds24-mapping-lookup";
import { withPreviewLinkPlaceholders } from "./preview-link-placeholders";
import { useIsMobile } from "../../lib/useIsMobile";
import SegmentEditor from "./segment-editor/SegmentEditor";
import {
  isRenderedOutsideEngine,
  RECEIPT_VAR_GROUPS,
} from "./segment-editor/audit-slugs";
import { isRoundTrippable, sameStructure } from "../../lib/message-segments";
// Libellés d'affichage des valeurs brutes (canal, condition) — table unique.
import {
  canalLabel,
  canalSentenceLabel,
  UNKNOWN_CONDITION_LABEL,
} from "./textes-auto-labels";

// 4 catégories (pastilles) — `proprio` toujours visible même sans template.
const CATEGORIES = [
  { id: "voyageur", label: "Voyageur" },
  { id: "admin", label: "Admin" },
  { id: "menage", label: "Ménage" },
  { id: "proprio", label: "Proprio" },
];

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

// ── Constantes Sends (importées depuis l'ancien RulesPanel) ────────

// Axe « QUAND » — ancres RÉELLES branchées au moteur (lib/messages-engine.js
// SUPPORTED_TRIGGERS). Seules celles-ci sont proposées : pas de placeholder
// « Phase X » (une entrée inerte se traduirait par une carte muette côté cron).
// Les 4 premières sont temporelles (offset + heure) ; les 2 dernières sont
// événementielles instantanées (création / annulation de réservation).
export const TRIGGER_TYPES = [
  { id: "temporal_checkin", label: "Avant / après l’arrivée", supported: true },
  {
    id: "temporal_checkout",
    label: "Avant / après le départ",
    supported: true,
  },
  {
    id: "temporal_booking",
    label: "Avant / après la date de réservation",
    supported: true,
  },
  { id: "temporal_fixed_date", label: "À une date précise", supported: true },
  {
    id: "event_booking_created",
    label: "Quand la réservation est créée",
    supported: true,
  },
  {
    id: "event_booking_cancelled",
    label: "Quand la réservation est annulée",
    supported: true,
  },
  // Chantier moteur méthode d'envoi (ADR-267) : caution validée (empreinte
  // Stripe autorisée OU toggle admin). Trigger réellement branché au moteur.
  {
    id: "event_caution_ok",
    label: "Quand la caution est validée",
    supported: true,
  },
];

// Ancres FUTURES — trigger_type existants côté moteur mais PAS encore émetteurs
// d'événement (contrat signé/consulté, formulaire bienvenue, online check-in,
// ménage assigné/retiré). Conservées ici comme mémo d'implémentation, HORS UI :
// jamais rendues, jamais un placeholder « Phase X » dans un dropdown. À
// déplacer dans TRIGGER_TYPES (supported:true) le jour où la source de
// l'événement est réellement branchée.
// eslint-disable-next-line no-unused-vars
const FUTURE_ANCHORS = [
  { id: "event_contract_signed", label: "Quand le contrat est signé" },
  { id: "event_contract_viewed", label: "Quand le contrat est consulté" },
  {
    id: "event_form_welcome_submitted",
    label: "Quand le formulaire de bienvenue est rempli",
  },
  {
    id: "event_online_checkin_submitted",
    label: "Quand le check-in en ligne est rempli",
  },
  { id: "event_menage_assigned", label: "Quand un ménage est assigné" },
  { id: "event_menage_unassigned", label: "Quand un ménage est retiré" },
];

export const CANALS = [
  { id: "email", label: canalLabel("email") },
  { id: "tchat_ota", label: canalLabel("tchat_ota") },
  { id: "notif_interne", label: canalLabel("notif_interne") },
  { id: "webpush", label: canalLabel("webpush"), disabled: true },
];

// Lot 3 — canaux RECOMMANDÉS par catégorie de message (pré-tri visuel ; TOUS les
// autres restent accessibles via « Autres canaux »). N'impose rien : un send
// existant garde son canal même hors reco (aucun send cassé).
export const RECOMMENDED_CANALS_BY_CATEGORY = {
  voyageur: ["tchat_ota", "email"],
  admin: ["notif_interne", "email"],
  menage: ["email", "notif_interne"],
  proprio: ["email"],
};
export function recommendedCanals(category) {
  return RECOMMENDED_CANALS_BY_CATEGORY[category] || ["email"];
}

export const COND_OPS = [
  { id: "eq", label: "=" },
  { id: "neq", label: "≠" },
  { id: "in", label: "dans" },
  { id: "gt", label: ">" },
  { id: "gte", label: "≥" },
  { id: "lt", label: "<" },
  { id: "lte", label: "≤" },
  { id: "is_null", label: "est vide" },
  { id: "is_not_null", label: "est rempli" },
];

export const CONDITION_FIELDS = [
  { id: "reservation.source", label: "Source (airbnb/booking/direct)" },
  { id: "reservation.status", label: "Statut résa (confirmed/cancelled…)" },
  // Re-mapping conditions : champs DÉRIVÉS source-aware (booléens true/false),
  // calculés par deriveConditionFields() avant l'éval (mirror payment-derivation).
  // À PRÉFÉRER aux timestamps ci-dessous (vides en prod).
  { id: "reservation.sejour_paye", label: "Séjour payé (dérivé, true/false)" },
  {
    id: "reservation.caution_recue",
    label: "Caution reçue (dérivé, true/false)",
  },
  // M5 (ADR-267) : champ dérivé du signal réel form_submissions (préchargé
  // batché par le moteur quand le send le référence).
  {
    id: "reservation.formulaire_rempli",
    label: "Formulaire de bienvenue rempli (dérivé, true/false)",
  },
  { id: "reservation.guest_country", label: "Pays voyageur" },
  { id: "reservation.guest_language", label: "Langue voyageur" },
  { id: "reservation.guest_count", label: "Nombre voyageurs" },
  { id: "reservation.total_amount", label: "Montant total (€)" },
  // Legacy Phase 2.B+ : timestamps NON renseignés en prod, conservés pour
  // compat affichage des anciennes conditions. Ne plus utiliser (préférer
  // sejour_paye / caution_recue dérivés).
  {
    id: "reservation.payment_received_at",
    label: "Paiement reçu à (legacy, vide en prod)",
  },
  {
    id: "reservation.caution_received_at",
    label: "Caution reçue à (legacy, vide en prod)",
  },
  { id: "logement.id", label: "ID logement" },
];

// ── Axe « SEULEMENT SI » — cases à cocher (Lot H) ──────────────────
// Les cases produisent des conditions {field,op,value} au format EXACT
// attendu par le moteur (0 changement engine) :
//   • Plateforme  → 1 condition {reservation.source, in, [ids cochés]}
//   • Statut      → 1 condition {reservation.status, in, [ids cochés]}
//   • Séjour payé → {reservation.sejour_paye, eq, true}   (champ dérivé)
//   • Caution reçue → {reservation.caution_recue, eq, true} (champ dérivé)
// OU intra-groupe = valeurs multiples d'un même `in`. ET inter-groupes =
// plusieurs entrées du tableau `conditions` (AND natif du moteur).
export const PLATFORM_OPTIONS = [
  { id: "airbnb", label: "Airbnb" },
  { id: "booking", label: "Booking" },
  { id: "direct", label: "Direct" },
];
export const STATUS_OPTIONS = [
  { id: "confirmed", label: "Confirmée" },
  { id: "cancelled", label: "Annulée" },
];
const PLATFORM_IDS = new Set(PLATFORM_OPTIONS.map((o) => o.id));
const STATUS_IDS = new Set(STATUS_OPTIONS.map((o) => o.id));

// deriveSendConditionState(conditions) → { state, advanced }
//   state    : { platforms:[], statuses:[], sejourPaye:false, cautionRecue:false }
//   advanced : conditions NON pilotables par cases (préservées telles quelles).
// Round-trip lossless : une condition n'est « absorbée » par une case que si
// son SENS correspond à une case (champ + valeur connus). Deux formes sont
// équivalentes et absorbées pour source/status :
//   • forme canonique      {field, op:'in',  value:['airbnb', …]}
//   • forme legacy scalaire {field, op:'eq',  value:'airbnb'}  ≡  in ['airbnb']
// (fix R1 audit templates : la QUASI-TOTALITÉ des message_sends prod stockent la
// forme `eq` scalaire — status eq 'confirmed', source eq 'airbnb' — que l'ancienne
// version laissait tomber en « Avancé », d'où des cases VIDES qui mentaient sur
// l'état réel + risque de doublon source/status contradictoire à la ré-écriture.)
// La forme `eq` absorbée N'apparaît PLUS dans « advanced » (sinon elle re-compterait
// en « hors cases » ET buildSendConditions ajouterait une seconde condition `in` du
// même champ à côté → deux conditions ANDées incompatibles = send muet). Au ré-save,
// buildSendConditions ré-écrit la forme canonique `in` (normalisation à l'écriture).
// Toute autre forme (op:'eq' valeur inconnue, autre champ, opérateur numérique,
// op:'neq'…) reste en « Avancé » et n'est jamais écrasée.
// Lot 3 — Paiement en TRI-ÉTATS (décision Dimitri + pessimist C2) :
//   'any' = aucune condition émise (peu importe)
//   'yes' = {field, eq, true}
//   'no'  = {field, eq, false}
// deriveSendConditionState absorbe désormais `eq true` ET `eq false`.
export function deriveSendConditionState(conditions) {
  const list = Array.isArray(conditions) ? conditions : [];
  const state = {
    platforms: [],
    statuses: [],
    sejourPaye: "any",
    cautionRecue: "any",
    formulaireRempli: "any",
  };
  const advanced = [];
  let seenPlatform = false,
    seenStatus = false,
    seenSejour = false,
    seenCaution = false,
    seenFormulaire = false;
  for (const c of list) {
    if (!seenPlatform && c && c.field === "reservation.source") {
      if (
        c.op === "in" &&
        Array.isArray(c.value) &&
        c.value.length > 0 &&
        c.value.every((v) => PLATFORM_IDS.has(v))
      ) {
        state.platforms = [...c.value];
        seenPlatform = true;
        continue;
      }
      // Legacy scalaire {source, eq, 'airbnb'} ≡ in ['airbnb'] (valeur connue only).
      if (c.op === "eq" && PLATFORM_IDS.has(c.value)) {
        state.platforms = [c.value];
        seenPlatform = true;
        continue;
      }
    }
    if (!seenStatus && c && c.field === "reservation.status") {
      if (
        c.op === "in" &&
        Array.isArray(c.value) &&
        c.value.length > 0 &&
        c.value.every((v) => STATUS_IDS.has(v))
      ) {
        state.statuses = [...c.value];
        seenStatus = true;
        continue;
      }
      // Legacy scalaire {status, eq, 'confirmed'} ≡ in ['confirmed'] (valeur connue only).
      if (c.op === "eq" && STATUS_IDS.has(c.value)) {
        state.statuses = [c.value];
        seenStatus = true;
        continue;
      }
    }
    if (
      !seenSejour &&
      c &&
      c.field === "reservation.sejour_paye" &&
      c.op === "eq" &&
      typeof c.value === "boolean"
    ) {
      state.sejourPaye = c.value ? "yes" : "no";
      seenSejour = true;
      continue;
    }
    if (
      !seenCaution &&
      c &&
      c.field === "reservation.caution_recue" &&
      c.op === "eq" &&
      typeof c.value === "boolean"
    ) {
      state.cautionRecue = c.value ? "yes" : "no";
      seenCaution = true;
      continue;
    }
    // Formulaire de bienvenue rempli (5e tri-état, M5/ADR-267) — copie EXACTE
    // du pattern caution : absorbe la forme {formulaire_rempli, eq, <boolean>}.
    // Toute autre forme (op `in`, valeur non-bool, sous un `or`) reste en Avancé.
    if (
      !seenFormulaire &&
      c &&
      c.field === "reservation.formulaire_rempli" &&
      c.op === "eq" &&
      typeof c.value === "boolean"
    ) {
      state.formulaireRempli = c.value ? "yes" : "no";
      seenFormulaire = true;
      continue;
    }
    advanced.push(c);
  }
  return { state, advanced };
}

// buildSendConditions(state, advanced) → conditions[] (cases d'abord, puis les
// conditions « Avancé » préservées à l'identique). Ordre stable → round-trip
// idempotent (l'AND du moteur est commutatif).
// La source/status écrites par les cases sont TOUJOURS canoniques `in` (tableau)
// → normalise au save toute forme `eq` scalaire absorbée en lecture.
// La case FAIT FOI pour source/status : si une case pilote ce champ (≥1 valeur
// cochée), toute condition « Avancé » du MÊME champ est REMPLACÉE (retirée) —
// sinon deux conditions du même champ ANDées se contredisent (send muet, R1).
// Les autres champs avancés sont préservés à l'identique.
export function buildSendConditions(state, advanced) {
  const out = [];
  const hasPlatforms =
    state && Array.isArray(state.platforms) && state.platforms.length > 0;
  const hasStatuses =
    state && Array.isArray(state.statuses) && state.statuses.length > 0;
  if (hasPlatforms)
    out.push({
      field: "reservation.source",
      op: "in",
      value: [...state.platforms],
    });
  if (hasStatuses)
    out.push({
      field: "reservation.status",
      op: "in",
      value: [...state.statuses],
    });
  // Tri-états paiement : 'yes' → eq true · 'no' → eq false · 'any'/absent → rien.
  if (state && state.sejourPaye === "yes")
    out.push({ field: "reservation.sejour_paye", op: "eq", value: true });
  else if (state && state.sejourPaye === "no")
    out.push({ field: "reservation.sejour_paye", op: "eq", value: false });
  if (state && state.cautionRecue === "yes")
    out.push({ field: "reservation.caution_recue", op: "eq", value: true });
  else if (state && state.cautionRecue === "no")
    out.push({ field: "reservation.caution_recue", op: "eq", value: false });
  // Formulaire de bienvenue rempli (5e tri-état) : émet LITTÉRALEMENT le champ
  // reservation.formulaire_rempli — chaîne load-bearing pour _sendNeedsFormData
  // (le préchargement batché moteur se déclenche sur la présence de cette chaîne).
  if (state && state.formulaireRempli === "yes")
    out.push({ field: "reservation.formulaire_rempli", op: "eq", value: true });
  else if (state && state.formulaireRempli === "no")
    out.push({
      field: "reservation.formulaire_rempli",
      op: "eq",
      value: false,
    });
  // « La case fait foi » — retire de l'Avancé les conditions qui feraient doublon
  // avec une case pilotée (sinon deux conditions ANDées se contredisent = send
  // muet, R1/R2). Distinction fine :
  //  • source/status : la case ré-écrit la forme canonique `in` → retire TOUTE
  //    condition simple du même champ (l'ancienne forme `eq`/`in` absorbée) ;
  //  • tri-états booléens (F1) : retire UNIQUEMENT la forme redondante EXACTE
  //    {op:'eq', value booléen} — jamais un `neq`/`in` de même champ (sens
  //    différent, perte silencieuse interdite). Les `or` (pas de .field) ne sont
  //    jamais visés par ce filtre.
  const sejourPiloted =
    state && state.sejourPaye !== "any" && state.sejourPaye != null;
  const cautionPiloted =
    state && state.cautionRecue !== "any" && state.cautionRecue != null;
  const formulairePiloted =
    state && state.formulaireRempli !== "any" && state.formulaireRempli != null;
  const isRedundantBool = (c, field) =>
    !!c && c.field === field && c.op === "eq" && typeof c.value === "boolean";
  const adv = (Array.isArray(advanced) ? advanced : []).filter((c) => {
    if (hasPlatforms && c && c.field === "reservation.source") return false;
    if (hasStatuses && c && c.field === "reservation.status") return false;
    if (sejourPiloted && isRedundantBool(c, "reservation.sejour_paye"))
      return false;
    if (cautionPiloted && isRedundantBool(c, "reservation.caution_recue"))
      return false;
    if (
      formulairePiloted &&
      isRedundantBool(c, "reservation.formulaire_rempli")
    )
      return false;
    // F3 — sanitizer défense-en-profondeur : DROP tout groupe OU vide/malformé
    // ({op:'or', conditions:[]}). Un `or` sans sous-condition s'évalue fail-closed
    // (send muet silencieux) côté moteur ; on ne l'écrit jamais.
    if (
      c &&
      c.op === "or" &&
      (!Array.isArray(c.conditions) || c.conditions.length === 0)
    )
      return false;
    return true;
  });
  return [...out, ...adv];
}

// ── Éditeur de groupe OU (cumul « au moins une de… ») — politique P-safe ──────
// Un groupe {op:'or', conditions:[…]} transite déjà verbatim par `advanced`
// (round-trip prouvé). Le rendre ÉDITABLE = muter le tableau `advanced` puis
// rappeler buildSendConditions — AUCUNE nouvelle sérialisation.
// P-safe : un groupe n'est éditable que si TOUTES ses sous-conditions sont
// pilotables (eq + booléen sur un champ dérivé connu). Sinon il reste read-only
// (préservé à l'octet), zéro risque de perte (or imbriqué, op numérique, champ
// inconnu → jamais édités).
export const OR_BOOL_FIELDS = [
  { id: "reservation.sejour_paye", label: "Séjour payé" },
  { id: "reservation.caution_recue", label: "Caution reçue" },
  {
    id: "reservation.formulaire_rempli",
    label: "Formulaire de bienvenue rempli",
  },
];
const OR_BOOL_FIELD_IDS = new Set(OR_BOOL_FIELDS.map((f) => f.id));

// Sous-condition pilotable = {field connu booléen, op:'eq', value booléen}.
export function isPilotableOrSubcond(c) {
  return (
    !!c &&
    OR_BOOL_FIELD_IDS.has(c.field) &&
    c.op === "eq" &&
    typeof c.value === "boolean"
  );
}
// Groupe OU éditable (P-safe) = op:'or' ∧ conditions non vide ∧ TOUTES pilotables.
export function isEditableOrGroup(c) {
  return (
    !!c &&
    c.op === "or" &&
    Array.isArray(c.conditions) &&
    c.conditions.length > 0 &&
    c.conditions.every(isPilotableOrSubcond)
  );
}
// Scinde l'`advanced` en (a) groupes OU éditables (avec leur index d'origine dans
// `advanced`, pour muter EN PLACE sans réordonner — R6 idempotence ordonnée) et
// (b) le reste, préservé read-only.
export function splitAdvanced(advanced) {
  const list = Array.isArray(advanced) ? advanced : [];
  const orGroups = [];
  const unknown = [];
  list.forEach((c, idx) => {
    if (isEditableOrGroup(c)) orGroups.push({ cond: c, idx });
    else unknown.push(c);
  });
  return { orGroups, unknown };
}

// ── Lot 3 — axe QUAND : offset signé ↔ (N absolu, sens) ────────────
// trigger_offset_value est SIGNÉ (négatif = AVANT l'ancre, positif = APRÈS).
// L'UI manipule N (valeur absolue) + un sens ('before' | 'after'). Cas de bord
// (test off-by-one) : 0 = « le jour même » (aucun décalage). joinOffset(0, *) = 0.
export function splitOffset(value) {
  const v = Number.isFinite(value) ? value : 0;
  if (v < 0) return { n: -v, direction: "before" };
  return { n: v, direction: "after" }; // v === 0 → jour même (sens neutre)
}
export function joinOffset(n, direction) {
  const abs = Math.abs(Number.isFinite(n) ? n : 0);
  if (abs === 0) return 0; // évite le piège -0 (Object.is(-0, 0) === false)
  return direction === "before" ? -abs : abs;
}
// Phrase de l'ancre temporelle pour la ligne « Envoyer N … avant/après … ».
export const TEMPORAL_ANCHOR_PHRASE = {
  temporal_checkin: "l’arrivée",
  temporal_checkout: "le départ",
  temporal_booking: "la date de réservation",
};
// Libellés d'unité (le modèle conserve trigger_offset_unit ; « jours » par défaut).
export const OFFSET_UNIT_LABEL = {
  minutes: "minute(s)",
  hours: "heure(s)",
  days: "jour(s)",
};
// Résumé lisible du « QUAND » d'un send (carte résumée + relecture).
export function sendQuandSummary(s) {
  if (!s) return "";
  const trig = TRIGGER_TYPES.find((t) => t.id === s.trigger_type);
  if (s.trigger_type === "temporal_fixed_date")
    return `Le ${s.trigger_fixed_date || "—"} à ${s.trigger_time || "—"}`;
  if ((s.trigger_type || "").startsWith("event_"))
    return `${trig?.label || s.trigger_type} — dans les 5 minutes qui suivent`;
  const { n, direction } = splitOffset(s.trigger_offset_value);
  const unit = OFFSET_UNIT_LABEL[s.trigger_offset_unit] || "jour(s)";
  const anchor = TEMPORAL_ANCHOR_PHRASE[s.trigger_type] || "";
  const when =
    n === 0
      ? "le jour même"
      : `${n} ${unit} ${direction === "before" ? "avant" : "après"} ${anchor}`;
  return `Envoyer ${when}, à ${s.trigger_time || "—"}`;
}

// ── Lot 4 (audit R2) — blocs plateforme jamais atteints ────────────
// Un bloc {#if is_airbnb|is_booking|is_direct} dans le body ne s'affichera
// jamais si TOUS les envois portent un filtre source qui EXCLUT cette plateforme.
// (Aucun avertissement si ≥1 envoi n'a pas de filtre source → il cible tout.)
const PLATFORM_IF_TO_ID = {
  is_airbnb: "airbnb",
  is_booking: "booking",
  is_direct: "direct",
};
const PLATFORM_ID_LABEL = {
  airbnb: "Airbnb",
  booking: "Booking",
  direct: "Direct",
};
export function platformBlocksInBody(str) {
  const out = new Set();
  if (!str) return out;
  const re = /\{#if\s+(is_airbnb|is_booking|is_direct)\}/g;
  let m;
  while ((m = re.exec(String(str))) !== null) out.add(PLATFORM_IF_TO_ID[m[1]]);
  return out;
}
export function unreachablePlatformBlocks(bodies, sends) {
  const blocks = new Set();
  for (const b of bodies || [])
    for (const x of platformBlocksInBody(b)) blocks.add(x);
  if (blocks.size === 0 || !Array.isArray(sends) || sends.length === 0)
    return [];
  // QA pessimist MINEUR-2 : un send draft/paused ne « couvre » pas réellement
  // une plateforme — seuls les envois actifs comptent pour l'atteignabilité.
  const activeSends = sends.filter((s) => s && isEngineActiveStatus(s.status));
  if (activeSends.length === 0) return [];
  const perSend = activeSends.map(
    (s) => deriveSendConditionState(s && s.conditions).state.platforms || [],
  );
  // ≥1 envoi SANS filtre source (cible toutes les plateformes) → aucun bloc mort.
  if (perSend.some((p) => p.length === 0)) return [];
  const covered = new Set();
  perSend.forEach((p) => p.forEach((x) => covered.add(x)));
  const out = [];
  for (const x of blocks) if (!covered.has(x)) out.push(x);
  return out;
}

// #4 — traduction lecture-seule du jargon condition en phrase FR claire pour
// non-technique. N'altère NI le stockage (message_sends.conditions) NI
// l'évaluation : pur affichage (résumé InlineSendCard + conditions avancées
// héritées en lecture seule).
const COND_FIELD_LABEL = {
  "reservation.source": "La source de la réservation",
  "reservation.status": "Le statut de la réservation",
  "reservation.sejour_paye": "Séjour payé",
  "reservation.caution_recue": "Caution reçue",
  "reservation.formulaire_rempli": "Formulaire de bienvenue rempli",
  "reservation.guest_country": "Le pays du voyageur",
  "reservation.guest_language": "La langue du voyageur",
  "reservation.guest_count": "Le nombre de voyageurs",
  "reservation.total_amount": "Le montant total",
  "reservation.payment_received_at": "La date de paiement",
  "reservation.caution_received_at": "La date de réception de caution",
  "logement.id": "Le logement",
};
const COND_OP_PHRASE = {
  eq: "est",
  neq: "n'est pas",
  in: "est l’un de",
  gt: "est supérieur à",
  gte: "est au moins",
  lt: "est inférieur à",
  lte: "est au plus",
};
function humanizeCondValue(v) {
  if (Array.isArray(v)) return v.join(" ou ");
  if (v === true) return "oui";
  if (v === false) return "non";
  return String(v);
}
export function humanizeCondition(c) {
  if (!c) return "";
  // OU logique (M5, ADR-267) : {op:'or', conditions:[…]} — affichage lisible
  // « (A OU B) ». Le moteur évalue en SOME ; l'UI préserve la condition telle
  // quelle en « Avancé » (pas de case pilotable).
  if (c.op === "or" && Array.isArray(c.conditions)) {
    const parts = c.conditions.map(humanizeCondition).filter(Boolean);
    return parts.length > 0 ? `(${parts.join(" OU ")})` : "";
  }
  if (!c.field) return "";
  // Champ sans libellé : jamais le nom technique brut à l'écran.
  if (!COND_FIELD_LABEL[c.field]) return UNKNOWN_CONDITION_LABEL;
  const field = COND_FIELD_LABEL[c.field];
  if (c.op === "is_null") return `${field} est vide`;
  if (c.op === "is_not_null") return `${field} est renseigné`;
  const op = COND_OP_PHRASE[c.op] || c.op;
  return `${field} ${op} ${humanizeCondValue(c.value)}`;
}

// LOT P3 — badge SOURCE de la carte d'envoi repliée (maquette Plateformes),
// DÉRIVÉ des conditions `reservation.source` (affichage SEUL — les conditions
// ne sont jamais modifiées ici). Univers affiché = OTA uniquement (ADR-266 :
// « direct » n'est jamais montré côté Plateformes, même si une condition
// legacy l'autorise encore). Aucune condition source → envoi universel →
// « AIRBNB + BOOKING ».
export function deriveSourceBadge(conds) {
  const OTA = ["airbnb", "booking"];
  const sourceConds = (Array.isArray(conds) ? conds : []).filter(
    (c) => c && c.field === "reservation.source",
  );
  let allowed = new Set([...OTA, "direct"]);
  for (const c of sourceConds) {
    let restrict;
    if (c.op === "eq") restrict = new Set([String(c.value)]);
    else if (c.op === "neq")
      restrict = new Set([...allowed].filter((x) => x !== String(c.value)));
    else if (c.op === "in" && Array.isArray(c.value))
      restrict = new Set(c.value.map(String));
    else continue;
    allowed = new Set([...allowed].filter((x) => restrict.has(x)));
  }
  const otas = OTA.filter((x) => allowed.has(x));
  if (otas.length === 2) return "AIRBNB + BOOKING";
  if (otas.length === 1) return otas[0].toUpperCase();
  return null;
}

// Lot visuel n°2 — plateformes OTA sur lesquelles le MESSAGE peut réellement
// partir : UNION sur ses envois (un envoi sans condition source = universel).
// Réutilise deriveSourceBadge (même sémantique que le moteur, ADR-266 : jamais
// « direct » ici). LECTURE SEULE — sert uniquement à filtrer les toggles de
// l'aperçu. Prudence : 0 envoi → les 2 plateformes (comportement historique).
export function allowedOtaPlatforms(sends) {
  const list = Array.isArray(sends) ? sends : [];
  if (list.length === 0) return ["airbnb", "booking"];
  const out = new Set();
  for (const s of list) {
    const badge = deriveSourceBadge(s?.conditions || []);
    if (badge === "AIRBNB + BOOKING") {
      out.add("airbnb");
      out.add("booking");
    } else if (badge === "AIRBNB") out.add("airbnb");
    else if (badge === "BOOKING") out.add("booking");
  }
  const arr = ["airbnb", "booking"].filter((x) => out.has(x));
  return arr.length > 0 ? arr : ["airbnb", "booking"];
}

// Lot visuel n°3 point 4 — PHRASE-RÉSUMÉ lisible de la carte d'envoi fermée
// (formule VALIDÉE Dimitri) : « Envoyé sur {plateformes} par {canal}, {quand},
// si {conditions} · {logements} ». HELPER PUR D'AFFICHAGE : lit trigger/canal/
// conditions, n'écrit JAMAIS rien — les conditions stockées et le moteur sont
// strictement intouchés.
const UNIT_FR = {
  minutes: ["minute", "minutes"],
  hours: ["heure", "heures"],
  days: ["jour", "jours"],
};
function humanQuand(s) {
  const t = s.trigger_type;
  if (t === "event_booking_created") return "dès que la réservation est créée";
  if (t === "event_booking_cancelled")
    return "dès que la réservation est annulée";
  if (t === "event_caution_ok") return "dès que la caution est validée";
  if (t === "event_caution_ko")
    return "dès que la caution est signalée en échec";
  const heure = s.trigger_time ? ` à ${s.trigger_time}` : "";
  if (t === "temporal_fixed_date") {
    if (!s.trigger_fixed_date) return `à une date précise${heure}`;
    try {
      const d = new Date(s.trigger_fixed_date + "T00:00:00");
      return `le ${d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}${heure}`;
    } catch (_) {
      return `le ${s.trigger_fixed_date}${heure}`;
    }
  }
  const ANCRE = {
    temporal_checkin: ["l'arrivée", "de l'arrivée"],
    temporal_checkout: ["le départ", "du départ"],
    temporal_booking: ["la réservation", "de la réservation"],
  };
  const anc = ANCRE[t];
  if (!anc) return t;
  const off = Number(s.trigger_offset_value || 0);
  if (off === 0) return `le jour ${anc[1]}${heure}`;
  const [sing, plur] = UNIT_FR[s.trigger_offset_unit || "days"] || UNIT_FR.days;
  const n = Math.abs(off);
  return `${n} ${n > 1 ? plur : sing} ${off < 0 ? "avant" : "après"} ${anc[0]}${heure}`;
}
const STATUS_FR = { confirmed: "confirmée", cancelled: "annulée" };
// Condition PILOTABLE → français ; sinon null (comptée « avancée »).
function humanCondFr(c) {
  if (!c || !c.field) {
    if (c && c.op === "or" && Array.isArray(c.conditions)) {
      const parts = c.conditions.map(humanCondFr);
      if (parts.every(Boolean)) return `(${parts.join(" ou ")})`;
    }
    return null;
  }
  const vals =
    c.op === "eq" || c.op === "neq"
      ? [c.value]
      : Array.isArray(c.value)
        ? c.value
        : null;
  if (
    c.field === "reservation.status" &&
    vals &&
    (c.op === "eq" || c.op === "in")
  ) {
    const labels = vals.map((v) => STATUS_FR[v]).filter(Boolean);
    if (labels.length === vals.length && labels.length > 0)
      return `réservation ${labels.join(" ou ")}`;
    return null;
  }
  if (
    c.field === "reservation.sejour_paye" &&
    c.op === "eq" &&
    typeof c.value === "boolean"
  ) {
    return c.value ? "séjour payé" : "séjour non payé";
  }
  if (
    c.field === "reservation.caution_recue" &&
    c.op === "eq" &&
    typeof c.value === "boolean"
  ) {
    return c.value ? "caution reçue" : "caution non reçue";
  }
  if (
    c.field === "reservation.formulaire_rempli" &&
    c.op === "eq" &&
    typeof c.value === "boolean"
  ) {
    return c.value ? "formulaire rempli" : "formulaire non rempli";
  }
  return null;
}
function joinFr(parts) {
  if (parts.length <= 1) return parts.join("");
  return parts.slice(0, -1).join(", ") + " et " + parts[parts.length - 1];
}
export function humanSendSummary(s, totalLogs) {
  const badge = deriveSourceBadge(s.conditions || []);
  const plats =
    badge === "AIRBNB + BOOKING"
      ? "Airbnb et Booking"
      : badge === "AIRBNB"
        ? "Airbnb"
        : badge === "BOOKING"
          ? "Booking"
          : null;
  const canal = canalSentenceLabel(s.canal);
  // Les conditions source sont dites par « sur {plateformes} » — on ne les
  // retire de la liste QUE si le badge les reflète (pessimist #945 MINEUR-2 :
  // une source hors-OTA — badge null — est comptée « avancée », jamais omise).
  const rest = (s.conditions || []).filter(
    (c) => !(c && c.field === "reservation.source" && plats !== null),
  );
  const human = [];
  let advanced = 0;
  for (const c of rest) {
    const h = humanCondFr(c);
    if (h) human.push(h);
    else advanced++;
  }
  let condPart;
  if (human.length > 0) {
    condPart = `, si ${joinFr(human)}`;
    if (advanced > 0)
      condPart += ` + ${advanced} réglage${advanced > 1 ? "s" : ""} particulier${advanced > 1 ? "s" : ""}`;
  } else if (advanced > 0) {
    condPart = `, + ${advanced} réglage${advanced > 1 ? "s" : ""} particulier${advanced > 1 ? "s" : ""}`;
  } else {
    condPart = ", pour toutes les réservations";
  }
  const n = (s.active_logement_ids || []).length;
  const logsPart =
    n === 0
      ? "aucun logement"
      : totalLogs != null && n >= totalLogs
        ? "tous les logements"
        : `${n} logement${n > 1 ? "s" : ""}${totalLogs != null ? ` sur ${totalLogs}` : ""}`;
  return `Envoyé${plats ? ` sur ${plats}` : ""} par ${canal}, ${humanQuand(s)}${condPart} · ${logsPart}`;
}

// SEND_STATUS_LABELS retiré Phase 2.D (plus utilisé après suppression
// du résumé compact / SendEditor — InlineSendCard rend les options
// directement dans son <select status>).

// Statuts ÉVALUÉS par le moteur (chantier moteur méthode d'envoi, ADR-267) :
// `active_catchup` = actif + rattrapage des occurrences temporelles passées.
// Utilisé partout où « actif » signifie « le cron évalue ce send » (opacité
// carte, atteignabilité des blocs plateforme, inertie).
export function isEngineActiveStatus(status) {
  return status === "active" || status === "active_catchup";
}

// ── Statut d'envoi simplifié — menu unique (status, catchup_mode) ──────
// Le send porte 2 champs : `status` ('draft'|'active'|'active_catchup'|'paused')
// et le nouveau `catchup_mode` ('full'|'reminder', défaut 'full', migration
// 20261028000001). Le menu unique les pilote ENSEMBLE via 4 choix métier.
// L'ancien 'active' (« à l'heure pile, jamais rattrapé ») est RETIRÉ de l'UI —
// il reste néanmoins reconnu en LECTURE (→ Essentiel) pour ne pas casser les
// sends legacy avant la migration de bascule (scripts/sql/flip-active-to-catchup).
export const SEND_STATUS_OPTIONS = [
  {
    key: "draft",
    label: "Brouillon",
    help: "Non évalué — n’est jamais envoyé.",
  },
  {
    key: "essentiel",
    label: "Essentiel — toujours envoyé, même en retard",
    help: "Part toujours, même pour une réservation de dernière minute (confirmation, instructions).",
  },
  {
    key: "rappel",
    label: "Rappel — envoyé sauf si trop tard",
    help: "Ne part pas si le moment prévu était déjà passé au moment de la réservation (J-2, J-1).",
  },
  {
    key: "paused",
    label: "En pause",
    help: "Temporairement désactivé.",
  },
];

// LECTURE : (status, catchup_mode) DB → clé d'option du menu.
//   draft → Brouillon ; paused → En pause ;
//   (active_catchup ∧ catchup_mode==='reminder') → Rappel ;
//   TOUT LE RESTE (active_catchup+full, legacy 'active', catchup_mode absent)
//   → Essentiel (fail-safe : un send legacy 'active' s'affiche « Essentiel »
//   sans bug avant migration).
export function sendStatusOptionKey(status, catchupMode) {
  if (status === "draft") return "draft";
  if (status === "paused") return "paused";
  if (status === "active_catchup" && catchupMode === "reminder")
    return "rappel";
  return "essentiel";
}

// ÉCRITURE : clé d'option → patch { status, catchup_mode } (un seul update objet).
// Brouillon/Pause préservent le catchup_mode courant (fallback 'full') pour ne
// jamais écraser silencieusement le mode d'un rappel simplement mis en pause.
export function sendStatusOptionPatch(key, currentCatchupMode) {
  const cur = currentCatchupMode === "reminder" ? "reminder" : "full";
  switch (key) {
    case "draft":
      return { status: "draft", catchup_mode: cur };
    case "paused":
      return { status: "paused", catchup_mode: cur };
    case "rappel":
      return { status: "active_catchup", catchup_mode: "reminder" };
    case "essentiel":
    default:
      return { status: "active_catchup", catchup_mode: "full" };
  }
}

// Ligne d'aide contextuelle SOUS le menu (change selon (status, catchup_mode)).
export function sendStatusHelp(status, catchupMode) {
  const key = sendStatusOptionKey(status, catchupMode);
  return SEND_STATUS_OPTIONS.find((o) => o.key === key)?.help || "";
}

// ── Lot 2 — validation d'un patch de traduction avant injection ────
// Le patch vient de l'endpoint message_translate ({subject_en, subject_es,
// body_en, body_es} — champs partiels possibles). Avant d'écrire quoi que ce
// soit à l'écran, CHAQUE champ traduit doit être round-trippable (pessimist
// M3) : si Claude a altéré une balise {#if}/{/if} au point de casser la
// structure, le champ est REJETÉ (non injecté) + un avertissement est produit —
// jamais d'injection silencieuse d'un contenu corrompu.
//
// ── B4-ext — DEUX contrôles, pas un seul ──────────────────────────────────
// Le cumul `{#if A ET B}` est désormais reconnu par le parseur (le message prod
// `demande_caution_formulaire` en contient 3, cf. golden). `isRoundTrippable`
// seule ne suffit donc PLUS comme filet : `{#if a AND b}` — opérateur traduit
// par l'IA — est parfaitement round-trippable, mais le moteur ne sait plus
// l'évaluer ⇒ le paragraphe conditionnel DISPARAÎT du mail EN/ES (et si l'IA
// renomme une opérande, c'est la MAUVAISE condition qui s'applique).
//
// D'où deux contrôles CUMULATIFS par champ :
//   1. round-trip (structure interne cohérente, aucune balise cassée) ;
//   2. MÊME STRUCTURE que le FR source (mêmes {var}, mêmes expressions {#if}
//      normalisées, même comptage) — jumeau client de `_sameStructure`
//      (lib/ai-translate.js), cf. `sameStructure` dans message-segments.js.
// Le contrôle 2 exige la source FR : sans elle (appel legacy à 1 argument), on
// dégrade au seul contrôle 1 et on le DIT dans le code plutôt que de laisser
// croire au filet complet.
const TRANSLATE_PARSE_OPTS = { allowCumul: true };
const TRANSLATE_FIELD_LABELS = {
  subject_en: "sujet EN",
  subject_es: "sujet ES",
  body_en: "message EN",
  body_es: "message ES",
};
// Champ traduit → champ FR de référence.
const TRANSLATE_SOURCE_FIELD = {
  subject_en: "subject_fr",
  subject_es: "subject_fr",
  body_en: "body_fr",
  body_es: "body_fr",
};
export function validateTranslationPatch(patch, source) {
  const toApply = {};
  const warnings = [];
  const src = patch && typeof patch === "object" ? patch : {};
  const ref = source && typeof source === "object" ? source : null;
  for (const key of ["subject_en", "subject_es", "body_en", "body_es"]) {
    if (!(key in src)) continue;
    const val = src[key];
    if (typeof val !== "string") continue;
    if (!isRoundTrippable(val, TRANSLATE_PARSE_OPTS)) {
      warnings.push(
        `La traduction du ${TRANSLATE_FIELD_LABELS[key]} a abîmé les infos insérées : elle n'a pas été reprise. À vérifier.`,
      );
      continue;
    }
    // Comparaison à la source FR : c'est CE contrôle qui attrape un `ET` traduit
    // en `AND`, une opérande renommée, une {var} inventée ou un bloc mangé.
    const frValue = ref ? ref[TRANSLATE_SOURCE_FIELD[key]] : null;
    if (typeof frValue === "string" && frValue.trim() !== "") {
      if (!sameStructure(frValue, val, TRANSLATE_PARSE_OPTS)) {
        warnings.push(
          `La traduction du ${TRANSLATE_FIELD_LABELS[key]} a abîmé les paragraphes conditionnels : elle n'a pas été reprise. À vérifier.`,
        );
        continue;
      }
    }
    toApply[key] = val;
  }
  return { toApply, warnings };
}

// ──────────────────────────────────────────────────────────────────

// LOT P2 — `onPreviewChange` (OPTIONNELLE) : remonte le brouillon vivant
// {tpl, activeLang} au parent pour l'aperçu en 3e colonne ; quand elle est
// fournie, l'aperçu interne historique est masqué (la colonne fait foi).
// Sans la prop (mobile, montages historiques) : comportement intact.
export default function MessageEditor({
  cfg,
  initial,
  onClose,
  onPreviewChange,
}) {
  // Responsive (Lot 0) — grilles denses → 1 colonne sous md (768).
  const isMobile = useIsMobile();
  const isNew = !initial;
  const [tpl, setTpl] = useState(
    () =>
      initial || {
        slug: "",
        category: "voyageur",
        name: "",
        description: "",
        body_fr: "",
        body_en: "",
        body_es: "",
        subject_fr: "",
        subject_en: "",
        subject_es: "",
        active: true,
      },
  );
  const [activeLang, setActiveLang] = useState("fr");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Lot 2 — traduction auto FR→EN+ES à la demande (synchrone, sans écriture DB).
  const [translating, setTranslating] = useState(false);
  const [translateWarnings, setTranslateWarnings] = useState([]);
  // pendingTranslate : patch validé EN ATTENTE de confirmation d'écrasement
  // (une version EN/ES est déjà non vide à l'écran). null = rien en attente.
  const [pendingTranslate, setPendingTranslate] = useState(null);
  // remountNonce : force le remontage des SegmentEditor après une mutation
  // PROGRAMMATIQUE de tpl.body_*/subject_* (injection de traduction). Sans ça,
  // le SegmentEditor — dont l'arbre est parsé UNE fois au montage et mémoïsé —
  // afficherait l'ancien contenu tant qu'on ne change pas de langue (état
  // périmé écran-vs-state). Bumpé à chaque applyTranslation (pessimist MAJEUR).
  const [remountNonce, setRemountNonce] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [previewResas, setPreviewResas] = useState([]);
  const [selectedResaId, setSelectedResaId] = useState("");
  const [resasLoading, setResasLoading] = useState(true);
  const [sends, setSends] = useState([]);
  const [sendsLoading, setSendsLoading] = useState(false);
  const [creatingNewSend, setCreatingNewSend] = useState(false);
  const [activeTab, setActiveTab] = useState("contenu"); // 'contenu' | 'envois'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: rsv } = await supabase
          .from("reservations")
          .select("*")
          .order("start_date", { ascending: false })
          .limit(30);
        if (cancelled) return;
        setPreviewResas(rsv || []);
        if ((rsv || []).length > 0) setSelectedResaId(rsv[0].id);
      } finally {
        if (!cancelled) setResasLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // LOT P2 — remonte le brouillon vivant à l'aperçu externe (3e colonne).
  // Prop optionnelle : sans elle, aucun effet (comportement historique).
  // useLayoutEffect (pessimist #937) : émission AVANT paint — au remontage
  // A→B (key), la colonne ne peut jamais peindre une frame avec le brouillon
  // périmé de A pendant que l'éditeur affiche déjà B.
  useLayoutEffect(() => {
    if (typeof onPreviewChange === "function")
      onPreviewChange({ tpl, activeLang });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl, activeLang]);

  // Charger les sends existants pour ce message (uniquement si !isNew)
  async function refreshSends() {
    if (isNew || !tpl.id) return;
    setSendsLoading(true);
    const { data } = await supabase
      .from("message_sends")
      .select("*")
      .eq("message_id", tpl.id)
      .order("created_at", { ascending: true });
    setSends(data || []);
    setSendsLoading(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refreshSends();
  }, [tpl.id]);

  // getAllLogsFlat descend dans cfg.props[].logs[].chambres[] (chambres sous-loc
  // Maison Zen p6 : lc1/lc2/lc3) — sinon la preview admin affiche des variables
  // logement vides pour les résas sur ces chambres.
  const allLogs = useMemo(() => getAllLogsFlat(cfg), [cfg]);
  const selectedResa =
    previewResas.find((r) => r.id === selectedResaId) || null;
  const selectedLogement = selectedResa
    ? allLogs.find((l) => l.id === selectedResa.logement_id) || null
    : null;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (!tpl.slug.trim() || !/^[a-z0-9_]+$/.test(tpl.slug.trim()))
        throw new Error(
          "Identifiant obligatoire (minuscules, chiffres et _ seulement)",
        );
      if (!tpl.name.trim()) throw new Error("Nom requis");
      if (!tpl.body_fr.trim())
        throw new Error("Le texte en français est obligatoire");

      const payload = { ...tpl, slug: tpl.slug.trim() };
      // Une variable FACULTATIVE nue hors de son {#if} ferait partir la phrase
      // sans elle (« excellent séjour à 🌸 ») : on le dit AVANT le clic, avec
      // le même message que le serveur — qui, lui, refuse pour de bon
      // (action message_save, jamais confiance au client).
      const verdict = verifierSecondairesGabarit(payload);
      if (!verdict.ok) throw new Error(verdict.erreurs[0].message);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch("/api/beds24-mapping?action=message_save", {
        method: "POST",
        headers: token
          ? {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }
          : { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "message_save",
          id: isNew ? null : tpl.id,
          message: payload,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          json?.message || json?.error || "Enregistrement échoué",
        );
      }
      // Important : recharger tpl avec l'id pour la section Sends (création).
      if (isNew && json?.message) setTpl(json.message);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // Champ (body/subject) mis à jour depuis SegmentEditor : la string re-sérialisée
  // (byte-identique si non modifiée) devient la nouvelle valeur du template.
  function setField(fieldKey, newValue) {
    setTpl((prev) => ({ ...prev, [fieldKey]: newValue }));
  }

  // Lot 2 — applique un patch de traduction aux champs EN/ES À L'ÉCRAN (state).
  // Rien n'est écrit en base ici : l'admin relit, puis Save persiste.
  function applyTranslation(toApply) {
    setTpl((prev) => ({ ...prev, ...toApply }));
    setPendingTranslate(null);
    // Mutation programmatique des champs → force le remontage des SegmentEditor
    // (sinon le champ de la langue affichée resterait périmé).
    setRemountNonce((n) => n + 1);
  }

  // Lot 2 — « Traduire automatiquement » : POST synchrone vers l'endpoint admin
  // message_translate, validation round-trip par champ, remplissage EN/ES à
  // l'écran. Si une version EN/ES est déjà non vide → confirmation inline avant
  // écrasement (jamais silencieux). Échec partiel : applique ce qui a réussi,
  // signale le reste champ par champ.
  async function translateAuto() {
    if (!tpl.id) {
      setError("Sauvegarde requise avant traduction");
      return;
    }
    setTranslating(true);
    setError(null);
    setTranslateWarnings([]);
    setPendingTranslate(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch("/api/beds24-mapping?action=message_translate", {
        method: "POST",
        headers: token
          ? {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }
          : { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "message_translate", id: tpl.id }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || "Traduction échouée");

      // La source FR est OBLIGATOIRE pour le 2e contrôle (structure comparée) :
      // sans elle, une traduction qui altère un {#if} passerait.
      const { toApply, warnings } = validateTranslationPatch(json?.patch, tpl);
      if (Object.keys(toApply).length === 0) {
        setTranslateWarnings(
          warnings.length ? warnings : ["Aucune traduction à injecter."],
        );
        return;
      }
      // Écrasement ? au moins un champ cible est déjà non vide à l'écran.
      const overwrites = Object.keys(toApply).some(
        (k) => (tpl[k] || "").trim() !== "",
      );
      setTranslateWarnings(warnings);
      if (overwrites) {
        setPendingTranslate(toApply); // attend confirmation inline
      } else {
        applyTranslation(toApply);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setTranslating(false);
    }
  }

  // R4 (audit) : slug rendu par un système dédié (reçus / documents) → variables
  // limitées, blocs conditionnels non interprétés (cf. audit-slugs.js).
  const renderedOutside = isRenderedOutsideEngine(tpl);
  const varGroups = renderedOutside
    ? RECEIPT_VAR_GROUPS
    : listAvailableVariables();

  // Liens générés à l'envoi (jeton, moteur serveur) : placeholder honnête en
  // aperçu — sinon {livret_url}/{welcome_form_url}/{paiement_caution_url}
  // rendent VIDE ici (ctx serveur absent côté client).
  const previewCtx = withPreviewLinkPlaceholders({
    reservation: selectedResa,
    logement: selectedLogement,
    cfg,
    language: activeLang,
  });
  const previewBody = renderTemplate(
    tpl["body_" + activeLang] || "",
    previewCtx,
  );
  const previewSubject = renderTemplate(
    tpl["subject_" + activeLang] || "",
    previewCtx,
  );

  // Badge ⚠️ « inerte de fait » : au moins un send n'est PAS (active ET ≥1 logement).
  // Reflète « ça n'envoie pas » sans changer la logique d'envoi (UI seule).
  const sendsInert = sends.some(
    (s) =>
      !(
        isEngineActiveStatus(s.status) &&
        (s.active_logement_ids || []).length > 0
      ),
  );

  // Lot 4 — blocs plateforme jamais atteints (bloc {#if is_X} mais aucun envoi
  // ne cible X). Union des 3 langues (les blocs sont censés être identiques).
  const unreachableBlocks = useMemo(
    () =>
      unreachablePlatformBlocks([tpl.body_fr, tpl.body_en, tpl.body_es], sends),
    [tpl.body_fr, tpl.body_en, tpl.body_es, sends],
  );

  return (
    <div
      className="fbz-card"
      style={{
        background: DS.card,
        borderRadius: DS.radiusCard,
        padding: 18,
        border: `0.5px solid ${DS.border}`,
      }}
    >
      {/* En-tête — nom éditable · catégories (pastilles) · badge statut · slug/description */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          {/* Nom éditable inline (crayon) */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {editingName || isNew ? (
              <input
                value={tpl.name}
                onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
                autoFocus
                placeholder="Nom du message"
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setEditingName(false);
                }}
                style={{
                  fontFamily: DS.fontDisplay,
                  fontSize: 19,
                  fontWeight: 600,
                  color: DS.marineDark,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: 8,
                  padding: "4px 10px",
                  outline: "none",
                  background: DS.card,
                  minWidth: 220,
                }}
              />
            ) : (
              <>
                <span
                  style={{
                    fontFamily: DS.fontDisplay,
                    fontSize: 19,
                    fontWeight: 600,
                    color: DS.marineDark,
                  }}
                >
                  {tpl.name || "Sans nom"}
                </span>
                <button
                  className="fbz-press"
                  onClick={() => setEditingName(true)}
                  aria-label="Renommer le message"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: DS.textMuted,
                    padding: 2,
                  }}
                >
                  <Pencil size={14} strokeWidth={2} />
                </button>
              </>
            )}
          </div>
          {/* Catégories en pastilles (proprio toujours visible) */}
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              marginTop: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, color: DS.textMuted }}>Pour :</span>
            {CATEGORIES.map((c) => {
              const on = tpl.category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className="fbz-press"
                  onClick={() => setTpl({ ...tpl, category: c.id })}
                  aria-pressed={on}
                  style={{
                    padding: "3px 12px",
                    border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                    borderRadius: 8,
                    background: on ? DS.marine : DS.card,
                    color: on ? "#fff" : DS.textSecondary,
                    fontSize: 12,
                    fontWeight: on ? 600 : 500,
                    cursor: "pointer",
                    fontFamily: DS.fontBody,
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Badge statut proéminent — 2 états (messages.active booléen) */}
            <button
              type="button"
              data-testid="status-badge"
              className="fbz-press"
              onClick={() => setTpl({ ...tpl, active: !tpl.active })}
              aria-pressed={tpl.active}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                border: `0.5px solid ${tpl.active ? DS.success : DS.borderHover}`,
                borderRadius: 10,
                padding: "7px 14px",
                background: tpl.active ? DS.successBg : DS.card,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: tpl.active ? DS.success : "transparent",
                  border: tpl.active ? "none" : `1.5px solid ${DS.textMuted}`,
                }}
              />
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: tpl.active ? DS.successText : DS.textSecondary,
                }}
              >
                {tpl.active ? "Actif" : "Inactif"}
              </span>
            </button>
            <button
              className="fbz-press"
              onClick={onClose}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: DS.card,
                color: DS.textSecondary,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: DS.radiusControl,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              <ArrowLeft size={14} strokeWidth={2} /> Retour
            </button>
          </div>
          {/* slug (immuable) + description repliés dans un disclosure discret */}
          <button
            type="button"
            onClick={() => setShowMeta((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 10.5,
              color: DS.textMuted,
              fontFamily: DS.fontBody,
              padding: 0,
            }}
          >
            identifiant :{" "}
            <code style={{ fontFamily: "monospace", color: DS.textSecondary }}>
              {tpl.slug || "—"}
            </code>{" "}
            · description
            {showMeta ? (
              <ChevronDown size={12} strokeWidth={2} />
            ) : (
              <ChevronRight size={12} strokeWidth={2} />
            )}
          </button>
        </div>
      </div>

      {showMeta && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr",
            gap: 10,
            marginBottom: 14,
            padding: "10px 12px",
            background: DS.pageBg,
            borderRadius: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                color: "#888",
                fontWeight: 700,
                marginBottom: 3,
                textTransform: "uppercase",
              }}
            >
              Identifiant (unique, ne change plus après la création)
            </div>
            <input
              value={tpl.slug}
              onChange={(e) =>
                setTpl({
                  ...tpl,
                  slug: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "_"),
                })
              }
              placeholder="ex : bienvenue_arrivee"
              disabled={!isNew}
              style={{
                width: "100%",
                border: "1px solid #dde3ec",
                borderRadius: 7,
                padding: "7px 10px",
                fontSize: 12,
                outline: "none",
                background: isNew ? "#f8fafd" : "#eef2f7",
                fontFamily: "monospace",
              }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 10,
                color: "#888",
                fontWeight: 700,
                marginBottom: 3,
                textTransform: "uppercase",
              }}
            >
              Description (interne)
            </div>
            <input
              value={tpl.description || ""}
              onChange={(e) => setTpl({ ...tpl, description: e.target.value })}
              placeholder="ex: J-1 à 20h"
              style={{
                width: "100%",
                border: "1px solid #dde3ec",
                borderRadius: 7,
                padding: "7px 10px",
                fontSize: 12,
                outline: "none",
                background: "#f8fafd",
              }}
            />
          </div>
        </div>
      )}

      {/* Barre d'onglets — Contenu / Envois (Chantier 9 Point 1, UI seule) */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 16,
          borderBottom: `2px solid ${DS.border}`,
        }}
      >
        <button
          onClick={() => setActiveTab("contenu")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "9px 18px",
            border: "none",
            borderBottom:
              activeTab === "contenu"
                ? `3px solid ${DS.corail}`
                : "3px solid transparent",
            background: "transparent",
            color: activeTab === "contenu" ? DS.marine : DS.textMuted,
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            marginBottom: -2,
            fontFamily: DS.fontBody,
          }}
        >
          <FileText size={15} strokeWidth={2} /> Contenu
        </button>
        <button
          onClick={() => setActiveTab("envois")}
          style={{
            padding: "9px 18px",
            border: "none",
            borderBottom:
              activeTab === "envois"
                ? `3px solid ${DS.corail}`
                : "3px solid transparent",
            background: "transparent",
            color: activeTab === "envois" ? DS.marine : DS.textMuted,
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            marginBottom: -2,
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontFamily: DS.fontBody,
          }}
        >
          <Send size={15} strokeWidth={2} /> Envois ({sends.length})
          {sendsInert && (
            <span
              data-testid="send-inert-badge"
              title="Au moins un envoi ne partira jamais : il est inactif ou n'a aucun logement coché"
              style={{ display: "inline-flex" }}
            >
              <AlertTriangle size={13} strokeWidth={2} color={DS.warning} />
            </span>
          )}
        </button>
      </div>

      {activeTab === "contenu" && (
        <div>
          {/* R4 (audit) — message rendu par un système dédié (reçus / documents) */}
          {renderedOutside && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "9px 12px",
                marginBottom: 12,
                background: DS.warningBg,
                border: `0.5px solid ${DS.warning}`,
                borderRadius: 9,
                fontSize: 11.5,
                color: DS.warningText,
                lineHeight: 1.5,
              }}
            >
              <FileText
                size={14}
                strokeWidth={2}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                Ce message sert aux reçus : seules les infos Prénom et Logement
                y fonctionnent, et les paragraphes conditionnels n'y sont pas
                pris en compte.
              </span>
            </div>
          )}

          {/* Lot 4 (audit R2) — bloc plateforme jamais atteint (aucun envoi ne cible X) */}
          {unreachableBlocks.map((x) => (
            <div
              key={x}
              data-testid={`unreachable-block-${x}`}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "9px 12px",
                marginBottom: 12,
                background: DS.warningBg,
                border: `0.5px solid ${DS.warning}`,
                borderRadius: 9,
                fontSize: 11.5,
                color: DS.warningText,
                lineHeight: 1.5,
              }}
            >
              <AlertTriangle
                size={14}
                strokeWidth={2}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                Ce bloc ne s'affichera jamais : aucun envoi ne cible{" "}
                <strong>{PLATFORM_ID_LABEL[x] || x}</strong>.
              </span>
            </div>
          ))}

          {/* Onglets langues + Traduire automatiquement (Lot 2, synchrone, sans écriture DB) */}
          <div
            style={{
              display: "flex",
              gap: 6,
              marginBottom: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {LANGS.map(({ code, label }) => (
              <button
                key={code}
                className="fbz-press"
                onClick={() => setActiveLang(code)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 14px",
                  border: `0.5px solid ${activeLang === code ? DS.marine : DS.border}`,
                  borderRadius: 20,
                  background: activeLang === code ? DS.marine : DS.card,
                  color: activeLang === code ? "#fff" : DS.textSecondary,
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                {label}
                {tpl["body_" + code] && (
                  <Check
                    size={11}
                    strokeWidth={2.4}
                    color={activeLang === code ? "#fff" : DS.success}
                  />
                )}
              </button>
            ))}
            {!isNew && (
              <button
                className="fbz-press"
                data-testid="translate-auto-btn"
                onClick={translateAuto}
                disabled={translating}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: 20,
                  background: "transparent",
                  color: translating ? DS.textMuted : DS.marine,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: translating ? "wait" : "pointer",
                  fontFamily: DS.fontBody,
                }}
                title="Traduit le FR vers EN + ES via IA — remplit les versions à l'écran (relire puis Enregistrer pour sauvegarder)"
              >
                <Languages size={14} strokeWidth={2} />{" "}
                {translating ? "Traduction…" : "Traduire automatiquement"}
              </button>
            )}
            <div style={{ flex: 1 }} />
          </div>

          {/* Lot 2 — confirmation inline AVANT écrasement des versions EN/ES existantes */}
          {pendingTranslate && (
            <div
              data-testid="translate-overwrite-confirm"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                marginBottom: 12,
                background: DS.warningBg,
                border: `0.5px solid ${DS.warning}`,
                borderRadius: 9,
                fontSize: 12,
                color: DS.warningText,
              }}
            >
              <AlertTriangle
                size={14}
                strokeWidth={2}
                style={{ flexShrink: 0 }}
              />
              <span style={{ flex: 1, minWidth: 180 }}>
                Écraser les versions EN/ES existantes à l'écran ?
              </span>
              <button
                className="fbz-press"
                onClick={() => setPendingTranslate(null)}
                style={{
                  background: DS.card,
                  color: DS.textSecondary,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "5px 12px",
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
              <button
                className="fbz-press"
                data-testid="translate-overwrite-apply"
                onClick={() => applyTranslation(pendingTranslate)}
                style={{
                  background: DS.corail,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "5px 14px",
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Écraser
              </button>
            </div>
          )}

          {/* Lot 2 — avertissements par champ (round-trip invalide / rien à injecter) */}
          {translateWarnings.length > 0 && (
            <div
              data-testid="translate-warnings"
              style={{
                padding: "8px 12px",
                marginBottom: 12,
                background: DS.dangerBg,
                border: `0.5px solid ${DS.dangerText}`,
                borderRadius: 9,
                fontSize: 11.5,
                color: DS.dangerText,
                lineHeight: 1.5,
              }}
            >
              {translateWarnings.map((w, i) => (
                <div
                  key={i}
                  style={{ display: "flex", gap: 6, alignItems: "flex-start" }}
                >
                  <AlertTriangle
                    size={12}
                    strokeWidth={2}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />{" "}
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* SUJET — segmenté mono-ligne (runs + pastilles, aucun encart {#if}) */}
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: 0.5,
                color: DS.textMuted,
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              SUJET{" "}
              <span style={{ fontWeight: 400, textTransform: "none" }}>
                (courriel uniquement — ignoré sur Airbnb et Booking)
              </span>
            </div>
            <SegmentEditor
              key={"subject_" + activeLang + "_" + remountNonce}
              variant="subject"
              value={tpl["subject_" + activeLang] || ""}
              onChange={(v) => setField("subject_" + activeLang, v)}
              varGroups={varGroups}
              allowBlocks={false}
            />
          </div>

          {/* MESSAGE — éditeur segmenté (runs + pastilles + encarts {#if})
          B4-ext : allowCumul — DÉCISION DIMITRI 2026-07-28. Le message de PROD
          `demande_caution_formulaire` (« Rappel J-2 unifié ») contient 3 cumuls
          ({#if is_booking ET formulaire_manquant}, + 2 sur la caution) : sans
          cette option, `{/if}` est orphelin → le corps bascule en LECTURE SEULE
          et le message n'est plus modifiable depuis l'admin. Cumul éditable
          préféré à un message bloqué en production. */}
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: 0.5,
                color: DS.textMuted,
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              MESSAGE
            </div>
            <SegmentEditor
              key={"body_" + activeLang + "_" + remountNonce}
              variant="body"
              value={tpl["body_" + activeLang] || ""}
              onChange={(v) => setField("body_" + activeLang, v)}
              varGroups={varGroups}
              allowBlocks={!renderedOutside}
              allowCumul
            />
          </div>

          {/* Preview */}
          {/* Aperçu interne historique — masqué quand l'aperçu vit en 3e colonne
          (LOT P2, onPreviewChange fourni) ; inchangé sinon (mobile). */}
          {!onPreviewChange && (
            <div
              style={{
                borderTop: "2px solid #eef2f7",
                paddingTop: 14,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    color: DS.marine,
                    textTransform: "uppercase",
                  }}
                >
                  <Eye size={14} strokeWidth={2} /> Aperçu
                </div>
                {!resasLoading && previewResas.length > 0 && (
                  <select
                    value={selectedResaId}
                    onChange={(e) => setSelectedResaId(e.target.value)}
                    style={{
                      border: "1px solid #dde3ec",
                      borderRadius: 7,
                      padding: "5px 8px",
                      fontSize: 11,
                      outline: "none",
                      background: "#f8fafd",
                    }}
                  >
                    {previewResas.map((r) => (
                      <option key={r.id} value={r.id}>
                        {(r.guest_first_name || "?") +
                          " " +
                          (r.guest_last_name || "")}{" "}
                        · {r.logement_id} · {r.start_date}
                      </option>
                    ))}
                  </select>
                )}
                {!resasLoading && previewResas.length === 0 && (
                  <span style={{ fontSize: 11, color: "#888" }}>
                    (aucune réservation disponible)
                  </span>
                )}
              </div>
              <div
                style={{
                  background: "#fff",
                  border: "1px dashed #dde3ec",
                  borderRadius: 8,
                  padding: 14,
                }}
              >
                {previewSubject && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 700,
                      color: DS.marineDark,
                      marginBottom: 8,
                      paddingBottom: 8,
                      borderBottom: `1px solid ${DS.border}`,
                    }}
                  >
                    <Mail size={14} strokeWidth={2} /> Sujet : {previewSubject}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 13,
                    color: "#333",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    fontFamily: DS.fontBody,
                  }}
                >
                  {previewBody || (
                    <span style={{ color: "#aaa", fontStyle: "italic" }}>
                      (texte vide en {activeLang.toUpperCase()})
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions content — le statut Actif/Inactif se pilote via le badge d'en-tête */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              borderTop: "1px solid #f0f4f8",
              paddingTop: 14,
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              {error && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 12px",
                    background: DS.dangerBg,
                    color: DS.dangerText,
                    borderRadius: 7,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  <AlertTriangle size={13} strokeWidth={2} /> {error}
                </div>
              )}
              <button
                className="fbz-press"
                onClick={save}
                disabled={saving}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  background: DS.corail,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "9px 22px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: saving ? "wait" : "pointer",
                  opacity: saving ? 0.6 : 1,
                  fontFamily: DS.fontBody,
                }}
              >
                <Save size={15} strokeWidth={2} />{" "}
                {saving
                  ? "…"
                  : isNew
                    ? "Créer le message"
                    : "Enregistrer le contenu"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "envois" && (
        <div>
          {/* Onglet Envois — hint si message pas encore créé (sends ont besoin de tpl.id) */}
          {isNew && (
            <div
              style={{
                padding: "16px 18px",
                background: DS.pageBg,
                color: DS.textSecondary,
                borderRadius: 10,
                fontSize: 12,
                textAlign: "center",
              }}
            >
              Créez d'abord le message (onglet Contenu) pour configurer les
              envois
            </div>
          )}

          {/* Section Envois programmés — édition inline (Phase 2.D) */}
          {!isNew && tpl.id && (
            <div style={{ paddingTop: 4 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                  gap: 10,
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontFamily: DS.fontDisplay,
                      fontSize: 15,
                      fontWeight: 600,
                      color: DS.marineDark,
                    }}
                  >
                    <Send size={16} strokeWidth={2} color={DS.marine} /> Envois
                    programmés ({sends.length})
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: DS.textSecondary,
                      marginTop: 2,
                    }}
                  >
                    Un envoi = un canal, un moment, et pour quels logements.
                    Vous pouvez en avoir plusieurs.
                  </div>
                </div>
                {!creatingNewSend && (
                  <button
                    className="fbz-press"
                    onClick={() => setCreatingNewSend(true)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: DS.corail,
                      color: "#fff",
                      border: "none",
                      borderRadius: DS.radiusControl,
                      padding: "7px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: DS.fontBody,
                    }}
                  >
                    <Plus size={14} strokeWidth={2.4} /> Nouvel envoi
                  </button>
                )}
              </div>

              {sendsLoading && (
                <div style={{ padding: 14, color: "#888", fontSize: 12 }}>
                  Chargement…
                </div>
              )}

              {/* Carte de création (en haut) — toujours en mode édition */}
              {creatingNewSend && (
                <InlineSendCard
                  cfg={cfg}
                  messageId={tpl.id}
                  category={tpl.category}
                  initial={null}
                  onSaved={() => {
                    setCreatingNewSend(false);
                    refreshSends();
                  }}
                  onCancel={() => setCreatingNewSend(false)}
                />
              )}

              {!sendsLoading && sends.length === 0 && !creatingNewSend && (
                <div
                  style={{
                    padding: "14px 16px",
                    background: "#f8fafd",
                    color: "#888",
                    borderRadius: 10,
                    fontSize: 12,
                    textAlign: "center",
                  }}
                >
                  Aucun envoi : ce message ne part jamais tant que vous ne lui
                  ajoutez pas un envoi.
                </div>
              )}

              {sends.map((s) => (
                <InlineSendCard
                  key={s.id}
                  cfg={cfg}
                  messageId={tpl.id}
                  category={tpl.category}
                  initial={s}
                  onSaved={refreshSends}
                  onDeleted={refreshSends}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── InlineSendCard — carte d'un send : RÉSUMÉ (lecture) ↔ ÉDITION (Lot 3) ──
// Une carte existante s'affiche en résumé (quand/canal/conditions/logements +
// Modifier/Supprimer/statut) ; « Modifier » ouvre l'édition en 4 questions
// (QUAND · CANAL · SEULEMENT SI · LOGEMENTS). Une nouvelle carte est en édition
// directe. Le stockage message_sends est INCHANGÉ (colonnes identiques).

const NEW_SEND_DEFAULTS = {
  trigger_type: "temporal_checkin",
  trigger_offset_value: -1,
  trigger_offset_unit: "days",
  trigger_time: "20:00",
  trigger_fixed_date: null,
  conditions: [],
  canal: "tchat_ota",
  destinataire_override: "",
  active_logement_ids: [],
  status: "draft",
  catchup_mode: "full",
};

// ── OrGroupEditor — éditeur d'un groupe OU pilotable (P-safe) ─────────────────
// Reçoit un groupe {op:'or', conditions:[{field,op:'eq',value:bool}, …]} dont
// TOUTES les sous-conditions sont pilotables (garanti par isEditableOrGroup en
// amont). Émet un nouveau groupe via onChange ; onRemove supprime le groupe.
// Invariant : jamais de groupe OU vide côté UI — retirer la dernière
// sous-condition appelle onRemove (F3 est une défense en profondeur en plus).
function OrGroupEditor({ group, onChange, onRemove }) {
  const subs = Array.isArray(group.conditions) ? group.conditions : [];
  function setSubField(i, field) {
    onChange({
      op: "or",
      conditions: subs.map((c, idx) => (idx === i ? { ...c, field } : c)),
    });
  }
  function setSubValue(i, value) {
    onChange({
      op: "or",
      conditions: subs.map((c, idx) =>
        idx === i ? { ...c, op: "eq", value } : c,
      ),
    });
  }
  function addSub() {
    onChange({
      op: "or",
      conditions: [
        ...subs,
        { field: "reservation.caution_recue", op: "eq", value: false },
      ],
    });
  }
  function removeSub(i) {
    const next = subs.filter((_, idx) => idx !== i);
    if (next.length === 0) {
      onRemove();
      return;
    } // jamais d'OU vide
    onChange({ op: "or", conditions: next });
  }
  return (
    <div
      data-testid="or-group-editor"
      style={{
        border: `0.5px solid ${DS.marine}`,
        borderRadius: 9,
        padding: "9px 11px",
        background: DS.pageBg,
        marginTop: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 7,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            color: DS.marine,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Au moins une de ces conditions
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Supprimer le groupe OU"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: DS.card,
            color: DS.dangerText,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "3px 8px",
            fontSize: 10.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <Trash2 size={11} strokeWidth={2} /> Groupe
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {subs.map((c, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            {i > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: DS.marine }}>
                OU
              </span>
            )}
            <select
              data-testid="or-sub-field"
              value={c.field}
              onChange={(e) => setSubField(i, e.target.value)}
              style={{
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 6,
                padding: "3px 6px",
                fontSize: 11,
                background: DS.card,
                fontFamily: DS.fontBody,
                color: DS.marineDark,
              }}
            >
              {OR_BOOL_FIELDS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
            <span style={{ display: "inline-flex", gap: 2 }}>
              {[
                [true, "Oui"],
                [false, "Non"],
              ].map(([v, label]) => {
                const on = c.value === v;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setSubValue(i, v)}
                    aria-pressed={on}
                    style={{
                      padding: "3px 9px",
                      border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                      background: on ? DS.marine : DS.card,
                      color: on ? "#fff" : DS.textMuted,
                      borderRadius: 6,
                      fontSize: 10.5,
                      fontWeight: on ? 700 : 500,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </span>
            <button
              type="button"
              onClick={() => removeSub(i)}
              aria-label="Retirer cette sous-condition"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: DS.card,
                color: DS.textMuted,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 6,
                padding: "3px 6px",
                fontSize: 10.5,
                cursor: "pointer",
              }}
            >
              <Trash2 size={11} strokeWidth={2} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addSub}
        data-testid="or-add-sub"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          marginTop: 7,
          background: "transparent",
          border: `0.5px dashed ${DS.borderHover}`,
          color: DS.marine,
          borderRadius: 6,
          padding: "3px 9px",
          fontSize: 10.5,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <Plus size={11} strokeWidth={2.4} /> Ajouter une condition
      </button>
    </div>
  );
}

function InlineSendCard({
  cfg,
  messageId,
  category,
  initial,
  onSaved,
  onDeleted,
  onCancel,
}) {
  // Responsive (Lot 0) — grilles → 1 colonne sous md (768).
  const isMobile = useIsMobile();
  const isNew = !initial;
  // Carte existante = RÉSUMÉ (lecture) par défaut ; « Modifier » ouvre l'édition.
  // Une nouvelle carte est directement en édition.
  const [editing, setEditing] = useState(isNew);
  const [s, setS] = useState(() =>
    initial ? { ...initial } : { ...NEW_SEND_DEFAULTS, message_id: messageId },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showOtherCanals, setShowOtherCanals] = useState(false);
  const [showRecipient, setShowRecipient] = useState(false);

  // getAllLogsFlat descend dans chambres sous-loc (lc1/lc2/lc3) — sinon
  // l'admin ne pourrait pas activer une règle ciblant ces chambres.
  const allLogs = useMemo(
    () => getAllLogsFlat(cfg).map((l) => ({ id: l.id, nom: l.nom })),
    [cfg],
  );
  const trig = TRIGGER_TYPES.find((t) => t.id === s.trigger_type);

  const dirty = isNew || JSON.stringify({ ...initial }) !== JSON.stringify(s);

  // Axe « SEULEMENT SI » — état dérivé de s.conditions (source de vérité unique).
  // Toggler = rebuild depuis l'état courant + conditions « Avancé » préservées.
  const { state: condState, advanced: advancedConds } = useMemo(
    () => deriveSendConditionState(s.conditions),
    [s.conditions],
  );

  function update(patch) {
    setS((prev) => ({ ...prev, ...patch }));
  }
  function togglePlatform(id) {
    const cur = condState.platforms;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    update({
      conditions: buildSendConditions(
        { ...condState, platforms: next },
        advancedConds,
      ),
    });
  }
  function toggleStatus(id) {
    const cur = condState.statuses;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    update({
      conditions: buildSendConditions(
        { ...condState, statuses: next },
        advancedConds,
      ),
    });
  }
  // Tri-états paiement : 'any' | 'yes' | 'no' (décision Dimitri + pessimist C2).
  function setSejourPaye(v) {
    update({
      conditions: buildSendConditions(
        { ...condState, sejourPaye: v },
        advancedConds,
      ),
    });
  }
  function setCautionRecue(v) {
    update({
      conditions: buildSendConditions(
        { ...condState, cautionRecue: v },
        advancedConds,
      ),
    });
  }
  // Formulaire de bienvenue — 5e tri-état ('any' | 'yes' | 'no'), même plumbing.
  function setFormulaireRempli(v) {
    update({
      conditions: buildSendConditions(
        { ...condState, formulaireRempli: v },
        advancedConds,
      ),
    });
  }
  // ── Groupes OU éditables (P-safe) — mutent `advanced` EN PLACE (index
  // préservé → ordre `advanced` stable, R6) puis rappellent buildSendConditions.
  const { orGroups, unknown: unknownAdvanced } = useMemo(
    () => splitAdvanced(advancedConds),
    [advancedConds],
  );
  function setOrGroupAt(idx, nextGroup) {
    const next = advancedConds.map((c, i) => (i === idx ? nextGroup : c));
    update({ conditions: buildSendConditions(condState, next) });
  }
  function removeAdvancedAt(idx) {
    const next = advancedConds.filter((_, i) => i !== idx);
    update({ conditions: buildSendConditions(condState, next) });
  }
  function addOrGroup() {
    // Jamais un groupe vide : on démarre avec 2 sous-conditions pilotables.
    const grp = {
      op: "or",
      conditions: [
        { field: "reservation.formulaire_rempli", op: "eq", value: false },
        { field: "reservation.caution_recue", op: "eq", value: false },
      ],
    };
    update({
      conditions: buildSendConditions(condState, [...advancedConds, grp]),
    });
  }
  function toggleLogement(id) {
    const cur = s.active_logement_ids || [];
    update({
      active_logement_ids: cur.includes(id)
        ? cur.filter((x) => x !== id)
        : [...cur, id],
    });
  }
  const allSelected =
    allLogs.length > 0 &&
    (s.active_logement_ids || []).length === allLogs.length;
  function toggleAllLogements() {
    update({
      active_logement_ids: allSelected ? [] : allLogs.map((l) => l.id),
    });
  }
  function reset() {
    setS(
      initial
        ? { ...initial }
        : { ...NEW_SEND_DEFAULTS, message_id: messageId },
    );
    setError(null);
  }
  function cancelEdit() {
    if (isNew) {
      onCancel && onCancel();
      return;
    }
    reset();
    setEditing(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...s, message_id: messageId };
      if (s.trigger_type !== "temporal_fixed_date")
        payload.trigger_fixed_date = null;
      // Normalise catchup_mode (jamais undefined ni valeur hors domaine) : un send
      // legacy chargé par select("*") avant migration n'a pas la colonne.
      payload.catchup_mode =
        s.catchup_mode === "reminder" ? "reminder" : "full";
      let res;
      if (isNew) {
        res = await supabase
          .from("message_sends")
          .insert(payload)
          .select()
          .maybeSingle();
      } else {
        res = await supabase
          .from("message_sends")
          .update(payload)
          .eq("id", s.id)
          .select()
          .maybeSingle();
      }
      if (res.error) throw new Error(res.error.message);
      if (!isNew) setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteSend() {
    if (
      !window.confirm(
        `Supprimer cet envoi (${trig?.label || s.trigger_type}) ?`,
      )
    )
      return;
    setSaving(true);
    setError(null);
    try {
      // `.select('id')` : une suppression qui ne trouve rien (ligne déjà
      // partie, RLS) doit se dire, pas se taire.
      const res = await supabase
        .from("message_sends")
        .delete()
        .eq("id", s.id)
        .select("id");
      const verdict = verifierEcriture(res, { canal: s.canal });
      if (!verdict.ok) throw new Error(verdict.message);
      onDeleted && onDeleted();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  async function setStatusOption(optionKey) {
    // Statut d'un send existant modifiable directement depuis le résumé.
    // Écrit status ET catchup_mode ensemble (un seul update objet).
    setSaving(true);
    setError(null);
    try {
      const patch = sendStatusOptionPatch(optionKey, s.catchup_mode);
      // ⚠️ Le cas le plus piégeux : sur un envoi SMS, cet UPDATE matche ZÉRO
      // ligne SANS erreur (policy admin_update_non_sms, `canal <> 'sms'`) — et
      // `update(patch)` juste en dessous mettait l'écran à jour comme si
      // c'était fait. Corrigé le 2026-09-08.
      const res = await supabase
        .from("message_sends")
        .update(patch)
        .eq("id", s.id)
        .select("id");
      const verdict = verifierEcriture(res, { canal: s.canal });
      if (!verdict.ok) throw new Error(verdict.message);
      update(patch);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const isTemporal = s.trigger_type.startsWith("temporal_");
  const isFixedDate = s.trigger_type === "temporal_fixed_date";
  const { n: offsetN, direction: offsetDir } = splitOffset(
    s.trigger_offset_value,
  );
  function setOffsetN(n) {
    update({ trigger_offset_value: joinOffset(n, offsetDir) });
  }
  function setOffsetDir(dir) {
    update({ trigger_offset_value: joinOffset(offsetN, dir) });
  }

  // Canaux : recommandés par catégorie + le canal COURANT (jamais masqué → aucun
  // send existant cassé) ; le reste accessible via « Autres canaux ».
  const recIds = recommendedCanals(category);
  const mainCanalIds = recIds.includes(s.canal) ? recIds : [...recIds, s.canal];
  const mainCanals = mainCanalIds
    .map((id) => CANALS.find((c) => c.id === id))
    .filter(Boolean);
  const otherCanals = CANALS.filter((c) => !mainCanalIds.includes(c.id));
  const activeCount = (s.active_logement_ids || []).length;
  const noLogement = activeCount === 0;

  // ═══════════════════════════════════════════════════════════════════════
  // AVERTISSEMENT « logement coché mais non raccordé » (2026-08-05)
  // ═══════════════════════════════════════════════════════════════════════
  // Cet écran laisse cocher les 11 logements, alors qu'un envoi de l'onglet
  // Plateformes se déclenche sur une réservation venue d'une OTA — donc via
  // Beds24. En production, SEULS lc1/lc2/lc3 ont un mapping Beds24 actif : un
  // message coché sur les 11 logements n'en couvre réellement que 3, et rien
  // ne le disait. On croyait un message actif partout ; il ne partait que pour
  // la sous-location.
  //
  // On AVERTIT, on ne bloque pas et on NE TOUCHE PAS AU MOTEUR : le mapping
  // peut être ajouté à tout moment, et la case cochée d'avance devient alors
  // effective sans rien avoir à refaire ici.
  const [mappedLogIds, setMappedLogIds] = useState(null); // null = pas encore lu
  useEffect(() => {
    let alive = true;
    getActiveMappedLogementIds()
      .then((ids) => {
        if (alive) setMappedLogIds(ids);
      })
      .catch((e) => {
        // Lecture d'information seulement : en cas d'échec on n'affiche aucun
        // avertissement plutôt qu'un avertissement potentiellement faux.
        console.warn(
          "[MessageEditor] lecture mapping Beds24 impossible:",
          e.message,
        );
        if (alive) setMappedLogIds(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  const unmappedSelected = useMemo(() => {
    if (!mappedLogIds) return [];
    const selected = s.active_logement_ids || [];
    return allLogs.filter(
      (l) => selected.includes(l.id) && !mappedLogIds.has(l.id),
    );
  }, [mappedLogIds, s.active_logement_ids, allLogs]);

  // ── Mode RÉSUMÉ (carte repliée — LOT P3 : accordéon façon maquette) ────
  // Carte CLIQUABLE (chevron) → déplie en mode édition. Badge SOURCE dérivé
  // des conditions (affichage seul), résumé HUMANISÉ (canal · conditions en
  // clair · logements). Le sélecteur de statut et Supprimer restent sur la
  // carte repliée (rien de perdu) — stopPropagation pour ne pas déplier.
  if (!editing && !isNew) {
    const conds = s.conditions || [];
    const srcBadge = deriveSourceBadge(conds);
    // Lot visuel n°2 (Dimitri) : couleur PAR SOURCE — Airbnb corail, Booking
    // marine ; universel = 2 mini-badges + liseré neutre. Fond de carte blanc.
    const SRC_TINTS = {
      AIRBNB: {
        label: "AIRBNB",
        bg: DS.corailSoft,
        fg: DS.corailText,
        edge: DS.corail,
      },
      BOOKING: {
        label: "BOOKING",
        bg: DS.cielSoft,
        fg: DS.marineDeep,
        edge: DS.marine,
      },
    };
    const srcBadges =
      srcBadge === "AIRBNB + BOOKING"
        ? [SRC_TINTS.AIRBNB, SRC_TINTS.BOOKING]
        : srcBadge
          ? [SRC_TINTS[srcBadge]]
          : [];
    const srcEdge = srcBadges.length === 1 ? srcBadges[0].edge : null;
    return (
      <div
        data-testid="send-edit-btn"
        role="button"
        tabIndex={0}
        className="fbz-rowhover"
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
        style={{
          border: `0.5px solid ${DS.border}`,
          borderLeft: `3px solid ${srcEdge || DS.border}`,
          borderRadius: 10,
          padding: 12,
          marginBottom: 8,
          background: DS.card,
          opacity: isEngineActiveStatus(s.status) ? 1 : 0.9,
          cursor: "pointer",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                flexWrap: "wrap",
              }}
            >
              {srcBadges.map((t) => (
                <span
                  key={t.label}
                  data-testid="send-source-badge"
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    color: t.fg,
                    background: t.bg,
                    padding: "2px 7px",
                    borderRadius: 5,
                  }}
                >
                  {t.label}
                </span>
              ))}
            </div>
            {/* Lot visuel n°3 point 4 : PHRASE-RÉSUMÉ (formule validée
                Dimitri) — remplace l'ancien résumé technique. */}
            <div
              data-testid="send-human-summary"
              style={{
                fontSize: 12.5,
                color: DS.textPrimary,
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              {humanSendSummary(s, allLogs.length)}
            </div>
          </div>
          {/* Finition Dimitri : wrap + shrink autorisés — le select statut
              (libellé catchup long) poussait poubelle/chevron HORS de la
              carte (flexShrink:0). maxWidth garde tout dans la carte. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              minWidth: 0,
              maxWidth: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <select
              data-testid="send-status-select"
              value={sendStatusOptionKey(s.status, s.catchup_mode)}
              onChange={(e) => setStatusOption(e.target.value)}
              disabled={saving}
              style={{
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: DS.radiusControl,
                padding: "5px 8px",
                fontSize: 11,
                background: DS.card,
                fontFamily: DS.fontBody,
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              {SEND_STATUS_OPTIONS.map((o) => (
                <option key={o.key} value={o.key} title={o.help}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              className="fbz-press"
              onClick={deleteSend}
              disabled={saving}
              aria-label="Supprimer l'envoi"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: DS.card,
                color: DS.dangerText,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: DS.radiusControl,
                padding: "5px 8px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
            </button>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                color: DS.textMuted,
                cursor: "pointer",
              }}
              onClick={() => setEditing(true)}
            >
              <ChevronDown size={16} strokeWidth={2} />
            </span>
          </div>
        </div>
        {/* Ligne d'aide contextuelle SOUS le menu de statut (change selon le choix). */}
        <div
          data-testid="send-status-help"
          style={{
            fontSize: 11,
            color: DS.textMuted,
            marginTop: 6,
            lineHeight: 1.4,
          }}
        >
          {sendStatusHelp(s.status, s.catchup_mode)}
        </div>
        {noLogement && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 8,
              fontSize: 11,
              color: DS.dangerText,
              fontWeight: 600,
            }}
          >
            <AlertTriangle size={12} strokeWidth={2} /> Aucun logement coché =
            cet envoi ne part jamais.
          </div>
        )}
        {error && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: DS.dangerText,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        border: `1px solid ${isNew ? DS.ciel : DS.border}`,
        borderRadius: 10,
        padding: 12,
        marginBottom: 8,
        opacity: s.status === "draft" ? 0.85 : 1,
        background: dirty ? DS.warningBg : DS.card,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11.5,
            fontWeight: 700,
            color: isNew ? DS.marine : DS.marineDark,
          }}
        >
          {isNew ? (
            <>
              <Plus size={13} strokeWidth={2.4} /> Nouvel envoi
            </>
          ) : (
            <>
              <Pencil size={12} strokeWidth={2} /> Modifier l'envoi
            </>
          )}
        </span>
        {dirty && !isNew && (
          <span
            style={{
              fontSize: 10,
              color: DS.warningText,
              fontWeight: 700,
              background: DS.warningBg,
              padding: "2px 7px",
              borderRadius: 5,
            }}
          >
            ● modifs non sauvées
          </span>
        )}
        <span style={{ flex: 1 }} />
        {!isNew && (
          <button
            className="fbz-press"
            onClick={deleteSend}
            disabled={saving}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: DS.card,
              color: DS.dangerText,
              border: `0.5px solid ${DS.borderHover}`,
              borderRadius: DS.radiusControl,
              padding: "5px 11px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <Trash2 size={13} strokeWidth={2} /> Supprimer
          </button>
        )}
      </div>

      {/* ── QUAND — ancres en pastilles + ligne de temporalité ─────────── */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 0.5,
            color: DS.marine,
            fontWeight: 700,
            marginBottom: 6,
            textTransform: "uppercase",
          }}
        >
          Quand envoyer ?
        </div>
        {/* Lot visuel n°3 (Dimitri) : 7 boutons → MENU DÉROULANT (1 ligne).
            Le onChange appelle EXACTEMENT le même update que l'ancien bouton,
            y compris la remise à 0 de l'offset sur les events (hygiène data,
            QA pessimist MINEUR-1) — preuve d'équivalence testée sur les 7
            types (payload de save identique). */}
        <select
          data-testid="send-trigger-select"
          value={s.trigger_type}
          onChange={(e) => {
            const id = e.target.value;
            update(
              id.startsWith("event_")
                ? { trigger_type: id, trigger_offset_value: 0 }
                : { trigger_type: id },
            );
          }}
          style={{
            width: "100%",
            marginBottom: 8,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 12.5,
            fontWeight: 600,
            color: DS.marineDark,
            background: DS.card,
            fontFamily: DS.fontBody,
          }}
        >
          {TRIGGER_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>

        {isTemporal && !isFixedDate && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
              background: DS.pageBg,
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "9px 12px",
              fontSize: 12.5,
              color: DS.textSecondary,
            }}
          >
            <span>Envoyer</span>
            <input
              type="number"
              min={0}
              value={offsetN}
              onChange={(e) => setOffsetN(parseInt(e.target.value, 10) || 0)}
              aria-label="Nombre"
              style={{
                width: 56,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            />
            <select
              value={s.trigger_offset_unit || "days"}
              onChange={(e) => update({ trigger_offset_unit: e.target.value })}
              aria-label="Unité"
              style={{
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            >
              <option value="minutes">minute(s)</option>
              <option value="hours">heure(s)</option>
              <option value="days">jour(s)</option>
            </select>
            {offsetN === 0 ? (
              <span style={{ fontWeight: 600, color: DS.marine }}>
                le jour même de {TEMPORAL_ANCHOR_PHRASE[s.trigger_type]}
              </span>
            ) : (
              <>
                <span style={{ display: "inline-flex", gap: 2 }}>
                  {["before", "after"].map((d) => {
                    const on = offsetDir === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setOffsetDir(d)}
                        aria-pressed={on}
                        style={{
                          padding: "3px 10px",
                          border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                          borderRadius: 7,
                          background: on ? DS.marine : DS.card,
                          color: on ? "#fff" : DS.textSecondary,
                          fontSize: 12,
                          fontWeight: on ? 700 : 500,
                          cursor: "pointer",
                        }}
                      >
                        {d === "before" ? "avant" : "après"}
                      </button>
                    );
                  })}
                </span>
                <span>{TEMPORAL_ANCHOR_PHRASE[s.trigger_type]}</span>
              </>
            )}
            <span>, à</span>
            <input
              value={s.trigger_time || ""}
              onChange={(e) => update({ trigger_time: e.target.value })}
              placeholder="10:00"
              aria-label="Heure"
              style={{
                width: 62,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            />
            <span style={{ fontSize: 11, color: DS.textMuted }}>
              (heure de Paris)
            </span>
          </div>
        )}

        {isFixedDate && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              background: DS.pageBg,
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "9px 12px",
              fontSize: 12.5,
              color: DS.textSecondary,
            }}
          >
            <span>Le</span>
            <input
              type="date"
              value={s.trigger_fixed_date || ""}
              onChange={(e) => update({ trigger_fixed_date: e.target.value })}
              aria-label="Date"
              style={{
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                background: DS.card,
              }}
            />
            <span>à</span>
            <input
              value={s.trigger_time || ""}
              onChange={(e) => update({ trigger_time: e.target.value })}
              placeholder="10:00"
              aria-label="Heure"
              style={{
                width: 62,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "4px 8px",
                fontSize: 12.5,
                fontWeight: 600,
                color: DS.marineDark,
                background: DS.card,
              }}
            />
            <span style={{ fontSize: 11, color: DS.textMuted }}>
              (heure de Paris)
            </span>
          </div>
        )}

        {/* Événements : aucun réglage de délai — le texte « part au prochain
            passage… » a été retiré (finition Dimitri ; réalité : created/
            cancelled quasi immédiats via webhook, caution ≤ 5 min via cron). */}
      </div>

      {/* ── CANAL — chips mono-choix (reco par catégorie) + autres canaux ── */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 0.5,
            color: DS.marine,
            fontWeight: 700,
            marginBottom: 6,
            textTransform: "uppercase",
          }}
        >
          Par quel canal ?
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {mainCanals.map((c) => {
            const on = s.canal === c.id;
            return (
              <button
                key={c.id}
                type="button"
                disabled={c.disabled}
                onClick={() => !c.disabled && update({ canal: c.id })}
                aria-pressed={on}
                style={{
                  padding: "4px 12px",
                  border: `0.5px solid ${on ? DS.green : DS.borderHover}`,
                  borderRadius: 8,
                  background: on ? "#E1F5EE" : DS.card,
                  color: c.disabled
                    ? DS.textMuted
                    : on
                      ? DS.green
                      : DS.textSecondary,
                  fontSize: 12,
                  fontWeight: on ? 700 : 500,
                  cursor: c.disabled ? "not-allowed" : "pointer",
                  fontFamily: DS.fontBody,
                  opacity: c.disabled ? 0.55 : 1,
                }}
              >
                {on ? "✓ " : ""}
                {c.label}
              </button>
            );
          })}
        </div>
        {otherCanals.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              onClick={() => setShowOtherCanals((v) => !v)}
              style={{
                background: "transparent",
                border: "none",
                color: DS.marine,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {showOtherCanals ? "▾" : "▸"} Autres canaux
            </button>
            {showOtherCanals && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginTop: 6,
                }}
              >
                {otherCanals.map((c) => {
                  const on = s.canal === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={c.disabled}
                      onClick={() => !c.disabled && update({ canal: c.id })}
                      aria-pressed={on}
                      style={{
                        padding: "4px 12px",
                        border: `0.5px solid ${on ? DS.green : DS.borderHover}`,
                        borderRadius: 8,
                        background: on ? "#E1F5EE" : DS.card,
                        color: c.disabled ? DS.textMuted : DS.textSecondary,
                        fontSize: 12,
                        fontWeight: on ? 700 : 500,
                        cursor: c.disabled ? "not-allowed" : "pointer",
                        fontFamily: DS.fontBody,
                        opacity: c.disabled ? 0.55 : 1,
                      }}
                    >
                      {on ? "✓ " : ""}
                      {c.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            onClick={() => setShowRecipient((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              color: DS.textMuted,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              padding: 0,
            }}
          >
            {showRecipient ? "▾" : "▸"} Envoyer à quelqu'un d'autre
          </button>
          {showRecipient && (
            <input
              value={s.destinataire_override || ""}
              onChange={(e) =>
                update({ destinataire_override: e.target.value })
              }
              placeholder="Destinataire spécifique (ex: contact@fullbnbzen.com)"
              style={{
                width: "100%",
                marginTop: 6,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7,
                padding: "6px 10px",
                fontSize: 12,
                background: DS.card,
              }}
            />
          )}
        </div>
      </div>

      {/* ── SEULEMENT SI — 3 mini-cartes (plateforme · statut · paiement) ── */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 0.5,
            color: DS.marine,
            fontWeight: 700,
            marginBottom: 6,
            textTransform: "uppercase",
          }}
        >
          Seulement si{" "}
          <span
            style={{
              fontWeight: 400,
              textTransform: "none",
              color: DS.textMuted,
            }}
          >
            (rien coché = toutes les réservations)
          </span>
        </div>
        {/* Lot visuel n°3 : 4 cartes homogènes sur grille AUTO-ADAPTATIVE
            (2×2 en colonne centre — plus de débordement en largeur). */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 8,
          }}
        >
          {/* Plateforme */}
          <div
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "8px 10px",
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: DS.textMuted,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Plateforme
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {PLATFORM_OPTIONS.filter(
                (o) =>
                  // LOT P4 (ADR-266) : « Direct » n'est plus proposé côté
                  // Plateformes — l'option ne s'affiche QUE si le send courant
                  // la porte déjà (condition héritée, décochable, jamais
                  // réécrite en douce — pattern « canal courant jamais masqué »).
                  o.id !== "direct" || condState.platforms.includes("direct"),
              ).map((o) => {
                const on = condState.platforms.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => togglePlatform(o.id)}
                    aria-pressed={on}
                    title={
                      o.id === "direct"
                        ? "Ancien réglage — les réservations directes ont leur propre parcours (volet Direct). Décochez pour le retirer au prochain enregistrement."
                        : undefined
                    }
                    style={{
                      padding: "3px 10px",
                      border: `0.5px solid ${on ? DS.green : DS.borderHover}`,
                      background: on ? "#E1F5EE" : DS.card,
                      color: on ? DS.green : DS.textMuted,
                      borderRadius: 7,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {on ? "☑ " : "☐ "}
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Statut */}
          <div
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "8px 10px",
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: DS.textMuted,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Statut
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {STATUS_OPTIONS.map((o) => {
                const on = condState.statuses.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggleStatus(o.id)}
                    aria-pressed={on}
                    style={{
                      padding: "3px 10px",
                      border: `0.5px solid ${on ? DS.green : DS.borderHover}`,
                      background: on ? "#E1F5EE" : DS.card,
                      color: on ? DS.green : DS.textMuted,
                      borderRadius: 7,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {on ? "☑ " : "☐ "}
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Paiement — TRI-ÉTATS */}
          <div
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "8px 10px",
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: DS.textMuted,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Séjour payé
            </div>
            <div
              data-testid="tristate-sejour"
              style={{ display: "flex", gap: 3 }}
            >
              {[
                ["any", "Peu importe"],
                ["yes", "Payé"],
                ["no", "Non payé"],
              ].map(([v, label]) => {
                const on = condState.sejourPaye === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setSejourPaye(v)}
                    aria-pressed={on}
                    style={{
                      flex: 1,
                      padding: "3px 4px",
                      border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                      background: on ? DS.marine : DS.card,
                      color: on ? "#fff" : DS.textMuted,
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: on ? 700 : 500,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* 4e carte : Caution (éclatée de l'ex-carte Paiement — même largeur
              que les autres, fini le débordement). */}
          <div
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "8px 10px",
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: DS.textMuted,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Caution reçue
            </div>
            <div
              data-testid="tristate-caution"
              style={{ display: "flex", gap: 3 }}
            >
              {[
                ["any", "Peu importe"],
                ["yes", "Reçue"],
                ["no", "Non reçue"],
              ].map(([v, label]) => {
                const on = condState.cautionRecue === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setCautionRecue(v)}
                    aria-pressed={on}
                    style={{
                      flex: 1,
                      padding: "3px 4px",
                      border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                      background: on ? DS.marine : DS.card,
                      color: on ? "#fff" : DS.textMuted,
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: on ? 700 : 500,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* 5e carte : Formulaire de bienvenue — TRI-ÉTATS (émet le champ dérivé
              reservation.formulaire_rempli, préchargé batché par le moteur). */}
          <div
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: 9,
              padding: "8px 10px",
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: DS.textMuted,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Formulaire de bienvenue
            </div>
            <div
              data-testid="tristate-formulaire"
              style={{ display: "flex", gap: 3 }}
            >
              {[
                ["any", "Peu importe"],
                ["yes", "Rempli"],
                ["no", "Non rempli"],
              ].map(([v, label]) => {
                const on = condState.formulaireRempli === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFormulaireRempli(v)}
                    aria-pressed={on}
                    style={{
                      flex: 1,
                      padding: "3px 4px",
                      border: `0.5px solid ${on ? DS.marine : DS.borderHover}`,
                      background: on ? DS.marine : DS.card,
                      color: on ? "#fff" : DS.textMuted,
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: on ? 700 : 500,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 6 }}>
          Rien coché = toutes les réservations qui atteignent ce moment.
        </div>

        {/* Groupes OU ÉDITABLES (P-safe) — « au moins une de… ». Les sous-conditions
            pilotables (eq + booléen) sont modifiables ; l'ordre dans `advanced`
            est préservé (mutation par index). */}
        {orGroups.map(({ cond, idx }) => (
          <OrGroupEditor
            key={idx}
            group={cond}
            onChange={(next) => setOrGroupAt(idx, next)}
            onRemove={() => removeAdvancedAt(idx)}
          />
        ))}
        {/* Ajouter un groupe OU (jamais vide — démarré avec 2 sous-conditions). */}
        <button
          type="button"
          onClick={addOrGroup}
          data-testid="add-or-group"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            marginTop: 8,
            background: "transparent",
            border: `0.5px dashed ${DS.marine}`,
            color: DS.marine,
            borderRadius: 8,
            padding: "5px 11px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          <Plus size={12} strokeWidth={2.4} /> Ajouter un groupe « au moins une
          de… »
        </button>

        {/* Conditions avancées héritées NON pilotables — LECTURE SEULE
            (préservées au save, ADR C1 · politique P-safe). */}
        {unknownAdvanced.length > 0 && (
          <div
            data-testid="advanced-conds-notice"
            style={{
              marginTop: 8,
              padding: "7px 10px",
              background: DS.pageBg,
              border: `0.5px solid ${DS.border}`,
              borderRadius: 8,
              fontSize: 11,
              color: DS.textSecondary,
            }}
          >
            <div style={{ fontWeight: 700, color: DS.marine, marginBottom: 3 }}>
              {unknownAdvanced.length} ancien(s) réglage(s) gardé(s) tel(s)
              quel(s)
            </div>
            {unknownAdvanced.map((c, i) => (
              <div key={i} style={{ color: "#666" }}>
                · {humanizeCondition(c)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── LOGEMENTS — « Tous » + chips + avertissement 0 logement ─────── */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 0.5,
            color: DS.marine,
            fontWeight: 700,
            marginBottom: 6,
            textTransform: "uppercase",
          }}
        >
          Pour quels logements ?
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 5,
            padding: "6px 8px",
            background: DS.pageBg,
            borderRadius: 8,
            maxHeight: 110,
            overflowY: "auto",
          }}
        >
          <button
            type="button"
            onClick={toggleAllLogements}
            aria-pressed={allSelected}
            style={{
              padding: "4px 10px",
              border: `0.5px solid ${allSelected ? DS.marine : DS.borderHover}`,
              background: allSelected ? DS.marine : DS.card,
              color: allSelected ? "#fff" : DS.textSecondary,
              borderRadius: 7,
              fontSize: 10.5,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {allSelected ? "☑ " : "☐ "}Tous
          </button>
          {allLogs.map((l) => {
            const on = (s.active_logement_ids || []).includes(l.id);
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => toggleLogement(l.id)}
                style={{
                  padding: "4px 8px",
                  border: `0.5px solid ${on ? DS.green : DS.borderHover}`,
                  background: on ? "#E1F5EE" : DS.card,
                  borderRadius: 7,
                  fontSize: 10,
                  cursor: "pointer",
                  color: on ? DS.green : DS.textMuted,
                  fontWeight: 600,
                }}
              >
                {on ? "☑ " : "☐ "}
                {l.nom}
              </button>
            );
          })}
        </div>
        {noLogement && (
          <div
            data-testid="no-logement-warning"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 6,
              fontSize: 11,
              color: DS.dangerText,
              fontWeight: 700,
            }}
          >
            <AlertTriangle size={13} strokeWidth={2} /> Aucun logement coché =
            cet envoi ne part jamais.
          </div>
        )}
        {/* Logements cochés mais SANS raccordement Beds24 : un envoi Plateformes
            se déclenche sur une réservation OTA, qui arrive par Beds24. Sans
            mapping, la case est cochée pour rien — l'envoi ne partira jamais
            pour ce logement. On prévient, on ne bloque pas. */}
        {unmappedSelected.length > 0 && (
          <div
            data-testid="unmapped-logement-warning"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 6,
              marginTop: 6,
              fontSize: 11,
              color: DS.warningText,
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            <AlertTriangle
              size={13}
              strokeWidth={2}
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <span>
              <strong>
                Cochés pour rien :{" "}
                {unmappedSelected.map((l) => l.nom).join(", ")}.
              </strong>{" "}
              Ces logements n'ont pas de raccordement Beds24 actif : aucune
              réservation de plateforme n'y arrive, donc cet envoi n'y partira
              jamais. Réellement couverts :{" "}
              {activeCount - unmappedSelected.length} sur {activeCount} cochés.
            </span>
          </div>
        )}
      </div>

      {/* Statut du send */}
      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            fontSize: 10,
            color: "#888",
            fontWeight: 700,
            marginBottom: 3,
            textTransform: "uppercase",
          }}
        >
          Statut de l'envoi
        </div>
        <select
          data-testid="send-status-select-edit"
          value={sendStatusOptionKey(s.status, s.catchup_mode)}
          onChange={(e) =>
            update(sendStatusOptionPatch(e.target.value, s.catchup_mode))
          }
          style={{
            width: "100%",
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 7,
            padding: "6px 10px",
            fontSize: 12,
            background: DS.card,
          }}
        >
          {SEND_STATUS_OPTIONS.map((o) => (
            <option key={o.key} value={o.key} title={o.help}>
              {o.label}
            </option>
          ))}
        </select>
        {/* Ligne d'aide contextuelle SOUS le menu (change selon le choix). */}
        <div
          data-testid="send-status-help-edit"
          style={{
            fontSize: 11,
            color: DS.textMuted,
            marginTop: 5,
            lineHeight: 1.4,
          }}
        >
          {sendStatusHelp(s.status, s.catchup_mode)}
        </div>
      </div>

      {/* Actions par carte */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          paddingTop: 8,
          borderTop: `0.5px solid ${DS.border}`,
        }}
      >
        {error && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "6px 10px",
              background: DS.dangerBg,
              color: DS.dangerText,
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <AlertTriangle size={12} strokeWidth={2} /> {error}
          </div>
        )}
        <button
          className="fbz-press"
          onClick={cancelEdit}
          style={{
            background: DS.card,
            color: DS.textSecondary,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          Annuler
        </button>
        <button
          data-testid="send-save-btn"
          className="fbz-press"
          onClick={save}
          disabled={saving || (!isNew && !dirty)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "6px 16px",
            fontSize: 12,
            fontWeight: 700,
            cursor: saving ? "wait" : "pointer",
            opacity: saving || (!isNew && !dirty) ? 0.5 : 1,
            fontFamily: DS.fontBody,
          }}
        >
          <Save size={13} strokeWidth={2} />{" "}
          {saving ? "…" : isNew ? "Créer" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

// ── condIsInScalar — garde exportée (réutilisée par les tests) ─────
// NB : l'ancienne ConditionsModal (édition brute {field,op,value}) est RETIRÉE
// au Lot 3 (l'axe « Seulement si » se pilote par cases/tri-états ; les
// conditions avancées héritées sont affichées en lecture seule et préservées au
// save via le plumbing `advanced` de derive/build). Le prédicat reste exporté.

// Garde anti-`in` scalaire (reco R6 audit) : l'opérateur `in` du moteur fait
// `Array.isArray(value) && value.includes(fieldValue)` → une string ne matchera
// JAMAIS (elle n'est pas un tableau). On refuse d'appliquer tant qu'un `in` porte
// une valeur non-tableau (ex. saisie `"airbnb"` au lieu de `["airbnb"]`).
export function condIsInScalar(c) {
  // `in` exige un tableau NON VIDE côté moteur ([].includes() = toujours faux
  // → condition muette permanente) : on signale scalaire ET tableau vide.
  return (
    !!c && c.op === "in" && (!Array.isArray(c.value) || c.value.length === 0)
  );
}
